import { useState, useEffect, useRef, useCallback } from "react";
import AdminPanel from "./AdminPanel";
import OwnerChat from "./OwnerChat";
import ProjectVisualizer from "./ProjectVisualizer";
import TradePreview from "./TradePreview";
import LandingHero from "./components/LandingHero";
import ServicesGrid from "./components/ServicesGrid";
import HowItWorks from "./components/HowItWorks";
import SiteFooter from "./components/SiteFooter";
import {
  submitQuote, sendChatMessage, saveChatSession,
  fetchQuotes, fetchProjects, fetchInvoices, createInvoice,
  sendInvoice, markInvoicePaid, uploadFile, updateQuoteStatus,
  generateDocument, fetchEmployees, fetchTimecards, parseTimecardFromText,
  approveTimecard, deleteTimecard, createEmployee, createCheckoutSession, sendContract, supabase,
  deleteQuote, deleteProject,
  fetchCapacity, fetchRevenueConfig, updateRevenueConfig,
} from "./lib/supabase";
import { useMarket } from "./context/MarketContext";
import { getMarket } from "./config/markets";
import COLORS from "./config/colors";
import { formatPhone } from "./lib/formatPhone";
import { calculateEstimate } from "./config/trades";
import Icons from "./components/Icons";
import StatusBadge from "./components/StatusBadge";
import AnimatedNumber from "./components/AnimatedNumber";
import BookingModal from "./components/modals/BookingModal";
import CreateInvoiceModal from "./components/modals/CreateInvoiceModal";
import MarkPaidModal from "./components/modals/MarkPaidModal";
import AddEmployeeModal from "./components/modals/AddEmployeeModal";
import PermitTracker from "./components/PermitTracker";
import CapacityDashboard from "./components/CapacityDashboard";

// ─── TRADE BACKGROUND ───
// Fixed full-screen background image per trade with dark overlay.
// Each image clearly shows the trade being performed or its finished result.
const TRADE_BG_IMAGES = {
  decks:    "1591825729269-0a1d0b3035a0", // composite deck with outdoor furniture and string lights
  concrete: "1558618666-fcd25c85f82e",     // concrete flatwork / patio with clean broom finish
  painting: "1589939705384-5185137a7f0f",  // painter rolling fresh paint on wall
  roofing:  "1635424709498-0088adde6b20",  // residential roof shingles, new installation
};
const _BG_BASE = "https://images.unsplash.com/photo-";
const _BG_PARAMS = "?w=1920&h=1080&fit=crop&q=80&auto=format";

function TradeBackground({ tradeSlug }) {
  const [currentImg, setCurrentImg] = useState(() => {
    const id = TRADE_BG_IMAGES[tradeSlug] || TRADE_BG_IMAGES.decks;
    return `${_BG_BASE}${id}${_BG_PARAMS}`;
  });
  const [nextImg, setNextImg] = useState(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const id = TRADE_BG_IMAGES[tradeSlug] || TRADE_BG_IMAGES.decks;
    const url = `${_BG_BASE}${id}${_BG_PARAMS}`;
    if (url === currentImg) return;
    // Crossfade to new trade image
    setNextImg(url);
    setFading(true);
    const timer = setTimeout(() => {
      setCurrentImg(url);
      setNextImg(null);
      setFading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [tradeSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: "none" }}>
      {/* Current image */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `url(${currentImg})`,
        backgroundSize: "cover", backgroundPosition: "center",
        opacity: fading ? 0 : 1,
        transition: "opacity 1.5s ease",
      }} />
      {/* Next image (crossfade target) */}
      {nextImg && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `url(${nextImg})`,
          backgroundSize: "cover", backgroundPosition: "center",
          opacity: fading ? 1 : 0,
          transition: "opacity 1.5s ease",
        }} />
      )}
      {/* Darkening overlay so content stays readable */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(180deg, rgba(20,12,6,0.55) 0%, rgba(20,12,6,0.40) 50%, rgba(20,12,6,0.65) 100%)",
      }} />
    </div>
  );
}


// COLORS imported from config/colors.js

// Materials, railing, and extras are loaded dynamically from the trade config (trades.js)
// via the `trade` prop. Fallback arrays kept for admin views that don't depend on trade.

// Sample data — no market-specific addresses
const SAMPLE_CUSTOMERS = [
  { id: 1, name: "Sample Customer A", email: "customer.a@email.com", phone: "(555) 555-0100", address: "123 Main St", status: "Active Project", project: "Full Deck Replacement", sqft: 480, material: "Trex Transcend", estimate: 18500, paid: 9250, notes: "Standard deck replacement project.", created: "2026-01-15" },
  { id: 2, name: "Sample Customer B", email: "customer.b@email.com", phone: "(555) 555-0101", address: "456 Oak Ave", status: "Estimate Sent", project: "New Deck + Pergola", sqft: 620, material: "Ipê Hardwood", estimate: 34200, paid: 0, notes: "New build with pergola add-on. HOA approval pending.", created: "2026-02-20" },
  { id: 3, name: "Sample Customer C", email: "customer.c@email.com", phone: "(555) 555-0102", address: "789 Pine Rd", status: "Completed", project: "Staircase & Railing", sqft: 0, material: "Cable Railing", estimate: 8900, paid: 8900, notes: "Custom staircase. Great referral source.", created: "2025-10-05" },
  { id: 4, name: "Sample Customer D", email: "customer.d@email.com", phone: "(555) 555-0103", address: "321 Elm St", status: "Lead", project: "Repair", sqft: 320, material: "TBD", estimate: 0, paid: 0, notes: "Needs site visit.", created: "2026-03-22" },
  { id: 5, name: "Sample Customer E", email: "customer.e@email.com", phone: "(555) 555-0104", address: "654 Cedar Blvd", status: "Active Project", project: "Multi-Level Deck", sqft: 850, material: "TimberTech AZEK", estimate: 42000, paid: 21000, notes: "Multi-level build with hot tub pad.", created: "2025-11-18" },
];

const SAMPLE_INVOICES = [
  { id: "INV-2026-001", customer: "Sample Customer A", amount: 9250, status: "Paid", date: "2026-02-01", due: "2026-02-15", description: "50% deposit - Deck Replacement" },
  { id: "INV-2026-002", customer: "Sample Customer E", amount: 21000, status: "Paid", date: "2026-01-20", due: "2026-02-04", description: "50% deposit - Multi-Level Deck" },
  { id: "INV-2026-003", customer: "Sample Customer B", amount: 17100, status: "Pending", date: "2026-03-01", due: "2026-03-15", description: "50% deposit - New Deck + Pergola" },
  { id: "INV-2026-004", customer: "Sample Customer A", amount: 9250, status: "Draft", date: "2026-03-28", due: "2026-04-11", description: "Final payment - Deck Replacement" },
];


// Icons, StatusBadge, AnimatedNumber imported from components/

// ─── OWNER CONTACT (from environment or market config) ───
const OWNER_NAME = process.env.REACT_APP_OWNER_NAME || "UltraLife Trades";
const OWNER_EMAIL = process.env.REACT_APP_OWNER_EMAIL || "info@ultralifetrades.com";
const OWNER_PHONE = process.env.REACT_APP_OWNER_PHONE || "";
const OWNER_WEBSITE = process.env.REACT_APP_OWNER_WEBSITE || "ultralifetrades.com";
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/ryanvukich/30min";

// ─── MAIN APP ───
export default function TradesPlatform({ market, trade, isAdmin = false, user, onLogout, LoginScreen }) {
  // Fallback to context if props not provided
  const marketCtx = useMarket();
  const activeMarket = market || marketCtx?.market;
  const activeTrade = trade || marketCtx?.trade;
  const { switchTrade } = marketCtx || {};

  // Trade-specific arrays from config
  const MATERIALS = activeTrade?.materials || [];
  const RAILING_OPTIONS = activeTrade?.railingOptions || [];
  const EXTRAS = activeTrade?.extras || [];
  const [currentView, setCurrentView] = useState("quote");
  const [showLogin, setShowLogin] = useState(false);

  // Invoice creation state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ customerName: '', customerEmail: '', customerAddress: '', description: '', lineItems: [{ description: '', amount: '' }], dueDate: '' });
  const [invoiceCreating, setInvoiceCreating] = useState(false);
  const [invoiceForCustomer, setInvoiceForCustomer] = useState(null); // tracks which CRM customer triggered the invoice

  // Estimate modal state
  const [showEstimateModal, setShowEstimateModal] = useState(false);
  const [estimateSending, setEstimateSending] = useState(false);

  // Mark as paid state
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ method: 'check', proofFile: null });
  const [paymentSaving, setPaymentSaving] = useState(false);

  // Sending invoice state
  const [sendingInvoiceId, setSendingInvoiceId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [fadeIn, setFadeIn] = useState(true);

  // Quote builder state
  const [quoteStep, setQuoteStep] = useState(0);
  const [deckSqFt, setDeckSqFt] = useState(400);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedRailing, setSelectedRailing] = useState(null);
  const [railingLf, setRailingLf] = useState(60);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerCity, setCustomerCity] = useState("");
  const [customerState, setCustomerState] = useState("");
  const [customerZip, setCustomerZip] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteId, setQuoteId] = useState(null);

  // Booking (Stripe deposit) state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [bookingName, setBookingName] = useState("");
  const [bookingEmail, setBookingEmail] = useState("");
  const [bookingPhone, setBookingPhone] = useState("");

  // AI Chat
  const [chatMessages, setChatMessages] = useState(() => [
    { role: "assistant", text: activeTrade?.chatGreeting || "Hi! I can help you plan your project — ask about materials, costs, timelines, or local code requirements." }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const chatEndRef = useRef(null);
  const quoteRef   = useRef(null);

  const scrollToQuote = useCallback(() => {
    quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Activity tracking
  const lastActivityRef = useRef(Date.now());

  // CRM state — live from Supabase
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [crmFilter, setCrmFilter] = useState("All");
  const [crmMarketFilter, setCrmMarketFilter] = useState("All");
  const [liveQuotes, setLiveQuotes] = useState([]);
  const [liveProjects, setLiveProjects] = useState([]);
  const [liveInvoices, setLiveInvoices] = useState([]);

  // Employee portal
  const [empView, setEmpView] = useState("timecards");

  // Track whether this is the initial mount (skip scroll-to-top on first load)
  const isInitialMount = useRef(true);
  const chatInitialized = useRef(false);

  useEffect(() => {
    // Skip scroll on initial greeting — only scroll for new messages after user interaction
    if (!chatInitialized.current) {
      chatInitialized.current = true;
      return;
    }
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [chatMessages]);


  // Reset quote configurator when trade changes
  useEffect(() => {
    if (!activeTrade) return;
    setDeckSqFt(activeTrade.sizeDefault || 400);
    setSelectedMaterial(null);
    setSelectedColor(null);
    setSelectedRailing(null);
    setSelectedExtras([]);
    setQuoteStep(0);
    setQuoteSubmitted(false);
    setQuoteError(null);
    setChatMessages([{ role: "assistant", text: activeTrade.chatGreeting || "Hi! How can I help you plan your project?" }]);
    chatInitialized.current = false; // reset so next greeting doesn't trigger page scroll
    // Scroll to top on trade switch (but not on initial page load)
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeTrade?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load live data from Supabase (only when admin — RLS requires auth)
  const loadData = useCallback(() => {
    if (!isAdmin) return;
    Promise.all([
      fetchQuotes({ completed: true }),
      fetchProjects(),
      fetchInvoices(),
    ]).then(([quotes, projects, invoices]) => {
      setLiveQuotes(quotes);
      setLiveProjects(projects);
      setLiveInvoices(invoices);
    });
  }, [isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  // Track user activity for abandonment detection
  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Track activity for future abandonment features
  useEffect(() => {
    if (!quoteSubmitted) trackActivity();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteStep]); // intentionally only runs on step change

  const switchView = (view) => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentView(view);
      setMobileMenuOpen(false);
      setFadeIn(true);
      // Refresh data when switching to admin views
      if (isAdmin && (view === "crm" || view === "invoices")) {
        loadData();
      }
    }, 200);
  };

  // Calculate estimate — uses shared calculateEstimate() from trades.js
  const estimate = calculateEstimate({
    trade: activeTrade,
    sqft: deckSqFt,
    material: selectedMaterial,
    railing: selectedRailing,
    railingLf,
    extras: selectedExtras,
  });
  const { total: totalEstimate, breakdown: estBreakdown } = estimate;
  const materialCost = estBreakdown.materials || 0;
  const laborCost = estBreakdown.labor || 0;
  const railingCost = estBreakdown.railing || 0;
  const extrasCost = estBreakdown.extras || 0;
  const overhead = estBreakdown.overhead || 0;

  // AI Chat handler — tries Claude API, falls back to local responses
  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatInput("");
    trackActivity();
    const updatedMessages = [...chatMessages, { role: "user", text: userMsg }];
    setChatMessages(updatedMessages);
    setChatLoading(true);

    // Build quote context for the AI
    const quoteContext = {
      sqft: deckSqFt,
      material: selectedMaterial?.name || null,
      color: selectedColor?.name || null,
      railing: selectedRailing?.name || null,
      extras: selectedExtras.map(e => e.name),
      estimateLow: Math.round(totalEstimate * 0.9),
      estimateHigh: Math.round(totalEstimate * 1.1),
    };

    // Try Claude API via edge function
    const apiMessages = updatedMessages
      .filter(m => m.role !== "system")
      .map(m => ({ role: m.role, text: m.text }));

    const aiResponse = await sendChatMessage(apiMessages, quoteContext, {
      marketSlug: activeMarket?.slug,
      marketDisplay: activeMarket?.displayName,
      marketState: activeMarket?.state,
      tradeDisplay: activeTrade?.displayName,
      buildingCodes: activeMarket?.buildingCodes?.[activeTrade?.slug] || '',
      pricingNotes: activeMarket?.pricingNotes || '',
      licenseNote: activeMarket?.licenseNote || '',
    });

    if (aiResponse) {
      setChatMessages(prev => [...prev, { role: "assistant", text: aiResponse }]);
      setChatLoading(false);
      // Save chat session (non-blocking)
      saveChatSession({
        id: chatSessionId,
        sessionType: "customer",
        quoteId,
        messages: [...updatedMessages, { role: "assistant", text: aiResponse }],
      }).then(saved => {
        if (saved?.id && !chatSessionId) setChatSessionId(saved.id);
      });
      return;
    }

    // Fallback: local keyword-based responses when API is unavailable
    setTimeout(() => {
      let response = "";
      const lower = userMsg.toLowerCase();
      const marketName = activeMarket?.displayName || "your area";
      const tradeName = activeTrade?.displayName || "your project";

      if (lower.includes("trex") || lower.includes("composite")) {
        response = "Trex composite decking is a great low-maintenance option — it handles freeze-thaw cycles and UV without the upkeep of natural wood. The Transcend line is the premium pick with deep wood grain patterns. Enhance is the budget-friendly entry point. Both carry strong warranties.";
      } else if (lower.includes("ipe") || lower.includes("hardwood")) {
        response = "Ipê is one of the densest hardwoods available — naturally rot-resistant with a 40+ year lifespan. It needs periodic oiling to maintain color, otherwise it weathers to silver (which many people love). It's the premium option but delivers exceptional longevity.";
      } else if (lower.includes("cedar") || lower.includes("redwood") || lower.includes("wood")) {
        response = "Natural wood gives you a classic, authentic look. Cedar is lighter and more affordable; Redwood has excellent durability with a beautiful grain. Both need sealing every 2-3 years in most climates. If you want the look without the maintenance, a composite in a wood tone is a great alternative.";
      } else if (lower.includes("cost") || lower.includes("price") || lower.includes("budget") || lower.includes("how much")) {
        response = `Based on your current selections, you're looking at roughly $${Math.round(totalEstimate).toLocaleString()} for a ${deckSqFt} sq ft ${activeTrade?.name || "project"}. That includes materials, labor, and overhead. We use fixed-bid contracts, so this estimate is what you'll pay — no surprise overruns. Want a breakdown?`;
      } else if (lower.includes("railing") || lower.includes("rail")) {
        response = "Cable railing is popular for view-preserving properties. Glass panels maximize the view but cost more. Wood railing gives a traditional look. Metal balusters are a solid mid-range option. What's the vibe of your space?";
      } else if (lower.includes("permit") || lower.includes("code") || lower.includes("building")) {
        const codeNote = activeMarket?.buildingCodes?.[activeTrade?.slug] || `Permits are typically required for ${tradeName} work above a certain size. Local codes vary — we'll help you navigate the permit process.`;
        response = `Good question for ${marketName}. ${codeNote}`;
      } else if (lower.includes("how long") || lower.includes("timeline") || lower.includes("schedule")) {
        response = `A typical ${activeTrade?.name || "project"} runs 1-4 weeks depending on scope. We'll give you a specific timeline after reviewing your project. We schedule in order of deposit receipt, so the sooner we connect the better.`;
      } else {
        response = `Great question! Use the quote builder to configure your ${activeTrade?.name || "project"}, then submit and we'll follow up within 24 hours with a detailed estimate for ${marketName}. Or ask me anything about materials, costs, timelines, or local requirements.`;
      }
      setChatMessages(prev => [...prev, { role: "assistant", text: response }]);
      setChatLoading(false);
    }, 1200);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      // Show local preview immediately
      const localUrl = URL.createObjectURL(file);
      const photoEntry = { name: file.name, url: localUrl, storageUrl: null, uploading: true };
      setUploadedPhotos(prev => [...prev, photoEntry]);

      // Upload to Supabase Storage in background
      const path = `quote-photos/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const storageUrl = await uploadFile('photos', path, file);

      setUploadedPhotos(prev => prev.map(p =>
        p.name === file.name && p.url === localUrl
          ? { ...p, storageUrl: storageUrl || null, uploading: false }
          : p
      ));
    }
  };

  // ─── BOOK PROJECT HANDLER ───
  const handleBookProject = async () => {
    const name = bookingName.trim();
    const email = bookingEmail.trim();
    const phone = bookingPhone.trim();

    if (!name || !email || !phone) {
      setBookingError("Please fill in your name, email, and phone number.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setBookingError("Please enter a valid email address.");
      return;
    }
    if (totalEstimate <= 0) {
      setBookingError("Please configure your project to get an estimate first.");
      return;
    }

    setBookingLoading(true);
    setBookingError(null);

    try {
      const fullAddress = [customerAddress, customerCity, customerState, customerZip]
        .filter(Boolean).join(", ");

      const result = await createCheckoutSession({
        total_price: totalEstimate,
        market: activeMarket?.slug,
        trade: activeTrade?.slug,
        customer_email: email,
        customer_name: name,
        customer_phone: phone,
        lead_data: {
          customerAddress: fullAddress || null,
          customerNotes: customerNotes || null,
          deckSqFt,
          materialId: selectedMaterial?.id || null,
          materialName: selectedMaterial?.name || null,
          colorSelection: selectedColor?.name || null,
          railingType: selectedRailing?.name || null,
          railingLf: selectedRailing?.id !== "none" ? railingLf : 0,
          extras: selectedExtras.map(e => ({ id: e.id, name: e.name, price: e.basePrice })),
          photoUrls: uploadedPhotos.map(p => p.storageUrl).filter(Boolean),
          estimateLow: Math.round(totalEstimate * 0.9),
          estimateHigh: Math.round(totalEstimate * 1.1),
          estimateBreakdown: { materialCost, laborCost, railingCost, extrasCost, overhead, total: totalEstimate },
        },
      });

      if (!result?.url) {
        throw new Error("Failed to create checkout session. Please try again.");
      }

      // Store booking summary in sessionStorage for the success page
      sessionStorage.setItem("ultralife_booking", JSON.stringify({
        customerName: name,
        trade: activeTrade?.displayName || activeTrade?.slug,
        market: activeMarket?.displayName,
        depositAmount: result.depositAmount || Math.round(totalEstimate * 0.165),
        totalEstimate: Math.round(totalEstimate),
        sessionId: result.sessionId,
      }));

      // Redirect to Stripe Checkout
      window.location.href = result.url;
    } catch (err) {
      console.error("Booking error:", err);
      setBookingError(
        err.message || `Something went wrong. Please contact us at ${OWNER_EMAIL}.`
      );
      setBookingLoading(false);
    }
  };

  const publicNavItems = [
    { id: "quote", label: "Get a Quote", icon: <Icons.Calculator /> },
  ];
  const adminNavItems = [
    { id: "quote", label: "Quote Builder", icon: <Icons.Calculator /> },
    { id: "crm", label: "Customers", icon: <Icons.Users /> },
    { id: "visualizer", label: "Drawings", icon: <Icons.Camera /> },
    { id: "invoices", label: "Invoicing", icon: <Icons.Invoice /> },
    { id: "employees", label: "Team Portal", icon: <Icons.Clock /> },
    { id: "capacity", label: "Capacity", icon: <Icons.Calculator /> },
    { id: "owner-chat", label: "AI Brain", icon: <Icons.Mountain /> },
    { id: "admin", label: "Admin", icon: <Icons.Star /> },
  ];
  const navItems = isAdmin ? adminNavItems : publicNavItems;

  // Build CRM customer list from live data
  // Every completed quote becomes a customer. Projects take priority.
  const crmCustomers = (() => {
    const customers = [];

    // Projects first
    for (const p of liveProjects) {
      const paidInvoices = liveInvoices.filter(i => i.project_id === p.id && i.status === "paid");
      const totalPaid = paidInvoices.reduce((s, i) => s + Number(i.total || 0), 0);
      customers.push({
        id: p.id, dbType: "project", name: p.customer_name, email: p.customer_email, phone: p.customer_phone,
        address: p.customer_address, status: p.status === "active" ? "Active Project" : p.status === "completed" ? "Completed" : "Lead",
        project: p.project_name, sqft: p.deck_sqft || 0, material: p.material || "TBD",
        estimate: p.bid_amount || 0, paid: totalPaid, notes: p.description || "", created: p.created_at,
        market_slug: p.market_slug || null, trade_slug: p.trade_slug || p.trade || null,
      });
    }

    // Quotes that didn't become projects
    for (const q of liveQuotes) {
      if (liveProjects.some(p => p.quote_id === q.id)) continue;
      const statusMap = { new: "Lead", contacted: "Lead", site_visit_scheduled: "Lead", bid_sent: "Estimate Sent", won: "Active Project", lost: "Completed" };
      customers.push({
        id: q.id, dbType: "quote", name: q.customer_name || "Unknown", email: q.customer_email, phone: q.customer_phone,
        address: q.customer_address, status: statusMap[q.status] || "Lead",
        project: `${q.material_name || "Deck"} — ${q.deck_sqft || "?"}sqft`, sqft: q.deck_sqft || 0,
        material: q.material_name || "TBD", estimate: q.estimate_high || 0, paid: 0,
        notes: q.customer_notes || "", created: q.created_at,
        market_slug: q.market_slug || null, trade_slug: q.trade_slug || q.trade || null,
      });
    }

    return customers.length > 0 ? customers : SAMPLE_CUSTOMERS;
  })();

  const filteredCustomers = crmCustomers
    .filter(c => crmFilter === "All" || c.status === crmFilter)
    .filter(c => crmMarketFilter === "All" || c.market_slug === crmMarketFilter);

  // Unique markets present in current CRM data — for the market filter pills.
  const crmMarketsPresent = Array.from(new Set(crmCustomers.map(c => c.market_slug).filter(Boolean))).sort();

  // ─── STYLES ───
  const styles = {
    app: { fontFamily: "'DM Sans', sans-serif", background: "transparent", minHeight: "100vh", color: COLORS.slate, display: "flex", flexDirection: "column" },
    header: { background: COLORS.bark, color: COLORS.snow, padding: "0 24px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 12px rgba(0,0,0,0.15)" },
    logo: { display: "flex", alignItems: "center", gap: 10 },
    logoText: { fontSize: 20, fontWeight: 700, letterSpacing: 1, fontFamily: "'Playfair Display', serif" },
    logoSub: { fontSize: 10, letterSpacing: 3, textTransform: "uppercase", opacity: 0.6, marginTop: -2 },
    nav: { display: "flex", gap: 2 },
    navBtn: (active) => ({ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 8, border: "none", background: active ? "rgba(196,145,59,0.2)" : "transparent", color: active ? COLORS.honey : COLORS.snow, cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 400, fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s", opacity: active ? 1 : 0.75 }),
    body: { flex: 1, display: "flex", flexDirection: "column", opacity: fadeIn ? 1 : 0, transition: "opacity 0.2s ease", position: "relative", zIndex: 2 },
    container: { maxWidth: 1400, margin: "0 auto", width: "100%", padding: "24px 20px" },
    card: { background: COLORS.glassBg, borderRadius: 12, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", border: "1px solid rgba(155,142,130,0.18)" },
    sectionTitle: { fontSize: 22, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.bark, marginBottom: 16 },
    label: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: COLORS.stone, marginBottom: 6 },
    input: { width: "100%", padding: "12px 16px", borderRadius: 10, border: `1px solid rgba(155,142,130,0.3)`, fontSize: 14, fontFamily: "'DM Sans', sans-serif", outline: "none", background: COLORS.cream, boxSizing: "border-box", transition: "border 0.2s" },
    textarea: { width: "100%", padding: "12px 16px", borderRadius: 10, border: `1px solid rgba(155,142,130,0.3)`, fontSize: 14, fontFamily: "'DM Sans', sans-serif", outline: "none", background: COLORS.cream, boxSizing: "border-box", minHeight: 80, resize: "vertical" },
    primaryBtn: { padding: "12px 28px", borderRadius: 10, border: "none", background: COLORS.honey, color: COLORS.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s", boxShadow: "0 2px 8px rgba(196,145,59,0.3)" },
    secondaryBtn: { padding: "10px 22px", borderRadius: 10, border: `2px solid ${COLORS.honey}`, background: "transparent", color: COLORS.honey, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s" },
    mobileMenuBtn: { background: "none", border: "none", color: COLORS.snow, cursor: "pointer", padding: 8, display: "none" },
  };

  // ─── RENDER QUOTE BUILDER ───
  const renderQuoteBuilder = () => (
    <div className="quote-grid" style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 24, alignItems: "start" }}>
      {/* LEFT: Quote Config */}
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Step indicators */}
        <div style={{ display: "flex", gap: 8 }}>
          {(activeTrade?.steps || ["Size & Photos", "Materials", "Options", "Your Info"]).map((step, i) => (
            <button key={i} onClick={() => setQuoteStep(i)} style={{ flex: 1, padding: "10px 8px", borderRadius: 10, border: `2px solid ${i === quoteStep ? COLORS.honey : "rgba(155,142,130,0.2)"}`, background: i === quoteStep ? "rgba(196,145,59,0.08)" : COLORS.white, color: i <= quoteStep ? COLORS.bark : COLORS.stone, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s" }}>
              <span style={{ display: "block", fontSize: 18, marginBottom: 2 }}>{i < quoteStep ? "✓" : i + 1}</span>
              {step}
            </button>
          ))}
        </div>

        {/* Step 0: Size & Photos */}
        {quoteStep === 0 && (
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>{activeTrade?.steps?.[0] || "Size & Photos"}</h3>
            <p style={{ color: COLORS.stone, fontSize: 14, marginBottom: 20 }}>Tell us about your space. Upload photos of your existing area and we'll help you plan the perfect {activeTrade?.name?.toLowerCase() || "project"}.</p>

            <div style={{ marginBottom: 24 }}>
              <label style={styles.label}>{activeTrade?.sizeLabel || "Project Size (sq ft)"}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <input type="range"
                  min={activeTrade?.sizeMin || 100}
                  max={activeTrade?.sizeMax || 2000}
                  step={activeTrade?.sizeStep || 25}
                  value={deckSqFt}
                  onChange={e => setDeckSqFt(Number(e.target.value))}
                  style={{ flex: 1, accentColor: COLORS.honey }} />
                <div style={{ background: COLORS.bark, color: COLORS.honey, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 18, minWidth: 80, textAlign: "center" }}>
                  {deckSqFt}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: COLORS.stone, marginTop: 4 }}>
                <span>{activeTrade?.sizeDescriptions?.min || "100 sf — small"}</span>
                <span>{activeTrade?.sizeDescriptions?.max || "2,000 sf — large"}</span>
              </div>
            </div>

            {/* Photo Upload */}
            <div>
              <label style={styles.label}>Upload Photos of Your Space</label>
              <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: 32, border: "2px dashed rgba(155,142,130,0.3)", borderRadius: 12, cursor: "pointer", background: "rgba(253,248,243,0.5)", transition: "all 0.2s" }}>
                <Icons.Upload />
                <span style={{ fontSize: 14, color: COLORS.stone }}>Drop photos here or click to browse</span>
                <span style={{ fontSize: 11, color: COLORS.stone, opacity: 0.6 }}>JPG, PNG up to 10MB each</span>
                <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} />
              </label>
              {uploadedPhotos.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 8, marginTop: 12 }}>
                  {uploadedPhotos.map((photo, i) => (
                    <div key={i} style={{ position: "relative", paddingTop: "100%", borderRadius: 8, overflow: "hidden", border: `1px solid ${photo.uploading ? COLORS.honey : photo.storageUrl ? COLORS.pine : "rgba(155,142,130,0.2)"}` }}>
                      <img src={photo.url} alt={photo.name} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", opacity: photo.uploading ? 0.6 : 1 }} />
                      {photo.uploading && (
                        <div style={{ position: "absolute", bottom: 4, left: 4, right: 4, textAlign: "center", fontSize: 10, color: COLORS.white, background: "rgba(196,145,59,0.8)", borderRadius: 4, padding: 2 }}>Uploading...</div>
                      )}
                      {photo.storageUrl && !photo.uploading && (
                        <div style={{ position: "absolute", bottom: 4, left: 4, width: 16, height: 16, borderRadius: "50%", background: COLORS.pine, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Icons.Check />
                        </div>
                      )}
                      <button onClick={() => setUploadedPhotos(prev => prev.filter((_, j) => j !== i))} style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => { setQuoteStep(1); scrollToQuote(); }} style={{ ...styles.primaryBtn, marginTop: 20, width: "100%", position: "relative", zIndex: 2 }}>
              Continue to Materials →
            </button>
          </div>
        )}

        {/* Step 1: Materials */}
        {quoteStep === 1 && (
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>{activeTrade?.steps?.[1] || "Choose Material"}</h3>
            <p style={{ color: COLORS.stone, fontSize: 14, marginBottom: 20 }}>All options below are proven performers for {activeMarket?.displayName || "your area"}'s climate and local building requirements.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {MATERIALS.map(mat => (
                <button key={mat.id} onClick={() => { setSelectedMaterial(mat); setSelectedColor(mat.colors[0]); }} style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, borderRadius: 12, border: `2px solid ${selectedMaterial?.id === mat.id ? COLORS.honey : "rgba(155,142,130,0.15)"}`, background: selectedMaterial?.id === mat.id ? "rgba(196,145,59,0.06)" : COLORS.white, cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s" }}>
                  {/* Material swatch */}
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: mat.swatch, flexShrink: 0, boxShadow: "inset 0 1px 3px rgba(0,0,0,0.2)", position: "relative", overflow: "hidden" }}>
                    {/* Wood grain lines */}
                    {[20, 35, 50, 65, 80].map(y => (
                      <div key={y} style={{ position: "absolute", top: `${y}%`, left: 0, right: 0, height: 1, background: "rgba(0,0,0,0.08)" }} />
                    ))}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: COLORS.bark }}>{mat.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.stone, marginTop: 2 }}>{mat.type} · {mat.colors.length} colors</div>
                    {/* Mini color dots */}
                    <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
                      {mat.colors.map(c => (
                        <div key={c.name} style={{ width: 12, height: 12, borderRadius: "50%", background: c.hex, border: "1px solid rgba(0,0,0,0.1)" }} />
                      ))}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: COLORS.honey }}>${mat.pricePerSqFt}</div>
                    <div style={{ fontSize: 11, color: COLORS.stone }}>/sq ft</div>
                  </div>
                </button>
              ))}
            </div>

            {selectedMaterial && (
              <div style={{ marginTop: 16 }}>
                <label style={styles.label}>Color Selection</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {selectedMaterial.colors.map(c => (
                    <button key={c.name} onClick={() => setSelectedColor(c)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 8, border: `2px solid ${selectedColor?.name === c.name ? COLORS.honey : "rgba(155,142,130,0.2)"}`, background: selectedColor?.name === c.name ? "rgba(196,145,59,0.1)" : COLORS.white, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s" }}>
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: c.hex, border: "1px solid rgba(0,0,0,0.1)", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15)" }} />
                      <span style={{ fontSize: 13, fontWeight: selectedColor?.name === c.name ? 600 : 400, color: COLORS.bark }}>{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => { setQuoteStep(0); scrollToQuote(); }} style={styles.secondaryBtn}>← Back</button>
              <button onClick={() => { setQuoteStep(2); scrollToQuote(); }} style={{ ...styles.primaryBtn, flex: 1 }}>Continue to {activeTrade?.steps?.[2] || "Options"} →</button>
            </div>
          </div>
        )}

        {/* Step 2: Options & Add-Ons (railings for decks, just add-ons for other trades) */}
        {quoteStep === 2 && (
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>{activeTrade?.steps?.[2] || "Options & Add-Ons"}</h3>

            {/* Railing section — decks only */}
            {activeTrade?.hasRailing && RAILING_OPTIONS.length > 0 && (
              <>
                <label style={styles.label}>Railing Style</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {RAILING_OPTIONS.map(rail => (
                    <button key={rail.id} onClick={() => setSelectedRailing(rail)} style={{ display: "flex", alignItems: "center", gap: 10, padding: 14, borderRadius: 10, border: `2px solid ${selectedRailing?.id === rail.id ? COLORS.honey : "rgba(155,142,130,0.15)"}`, background: selectedRailing?.id === rail.id ? "rgba(196,145,59,0.06)" : COLORS.glassBg, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}>
                      {rail.swatch && (
                        <div style={{ width: 32, height: 32, borderRadius: 6, background: rail.swatch, flexShrink: 0, border: "1px solid rgba(0,0,0,0.1)", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)", opacity: rail.id === "glass" ? 0.5 : 1 }} />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.bark }}>{rail.name}</div>
                        <div style={{ fontSize: 13, color: COLORS.honey, fontWeight: 600 }}>{rail.pricePerLf > 0 ? `$${rail.pricePerLf}/lf` : "—"}</div>
                      </div>
                    </button>
                  ))}
                </div>
                {selectedRailing && selectedRailing.id !== "none" && (
                  <div style={{ marginBottom: 20 }}>
                    <label style={styles.label}>Linear Feet of Railing</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <input type="range" min="10" max="200" value={railingLf} onChange={e => setRailingLf(Number(e.target.value))} style={{ flex: 1, accentColor: COLORS.honey }} />
                      <span style={{ fontWeight: 700, color: COLORS.bark, minWidth: 50 }}>{railingLf} lf</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Concrete: thickness and demo options */}
            {activeTrade?.slug === "concrete" && (
              <div style={{ marginBottom: 20 }}>
                <label style={styles.label}>Slab Thickness</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {[
                    { id: "4in", label: '4" Standard', desc: "Patios, walkways, pool decks", price: 0 },
                    { id: "6in", label: '6" Heavy Duty', desc: "Driveways, heavy loads", price: 2 },
                  ].map(opt => {
                    const isSelected = (selectedRailing?.id || "4in") === opt.id;
                    return (
                      <button key={opt.id} onClick={() => setSelectedRailing({ id: opt.id, name: opt.label, pricePerLf: 0, priceAdder: opt.price })}
                        style={{ padding: 14, borderRadius: 10, border: `2px solid ${isSelected ? COLORS.honey : "rgba(155,142,130,0.15)"}`, background: isSelected ? "rgba(196,145,59,0.06)" : COLORS.glassBg, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.bark }}>{opt.label}</div>
                        <div style={{ fontSize: 12, color: COLORS.stone, marginTop: 2 }}>{opt.desc}</div>
                        {opt.price > 0 && <div style={{ fontSize: 13, color: COLORS.honey, fontWeight: 600, marginTop: 4 }}>+${opt.price}/sq ft</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Painting: interior/exterior toggle */}
            {activeTrade?.slug === "painting" && (
              <div style={{ marginBottom: 20 }}>
                <label style={styles.label}>Project Type</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {[
                    { id: "interior", label: "Interior", desc: "Walls, ceilings, trim" },
                    { id: "exterior", label: "Exterior", desc: "Siding, trim, fascia" },
                  ].map(opt => {
                    const isSelected = (selectedRailing?.id || "exterior") === opt.id;
                    return (
                      <button key={opt.id} onClick={() => setSelectedRailing({ id: opt.id, name: opt.label, pricePerLf: 0 })}
                        style={{ padding: 14, borderRadius: 10, border: `2px solid ${isSelected ? COLORS.honey : "rgba(155,142,130,0.15)"}`, background: isSelected ? "rgba(196,145,59,0.06)" : COLORS.glassBg, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: COLORS.bark }}>{opt.label}</div>
                        <div style={{ fontSize: 12, color: COLORS.stone, marginTop: 2 }}>{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
                <label style={styles.label}>Stories</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                  {["1 Story", "2 Stories", "3 Stories"].map((s, i) => {
                    const isSelected = railingLf === i + 1;
                    return (
                      <button key={s} onClick={() => setRailingLf(i + 1)}
                        style={{ padding: 12, borderRadius: 10, border: `2px solid ${isSelected ? COLORS.honey : "rgba(155,142,130,0.15)"}`, background: isSelected ? "rgba(196,145,59,0.06)" : COLORS.glassBg, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13, color: isSelected ? COLORS.bark : COLORS.stone }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Roofing: tear-off toggle + stories */}
            {activeTrade?.slug === "roofing" && (
              <div style={{ marginBottom: 20 }}>
                <label style={styles.label}>Roof Stories / Pitch</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {[
                    { id: "1story", label: "1 Story", desc: "Easy access", priceAdder: 0 },
                    { id: "2story", label: "2 Stories", desc: "Standard access", priceAdder: 1 },
                    { id: "3story", label: "3 Stories", desc: "Complex access", priceAdder: 2 },
                  ].map(opt => {
                    const isSelected = (selectedRailing?.id || "1story") === opt.id;
                    return (
                      <button key={opt.id} onClick={() => setSelectedRailing({ id: opt.id, name: opt.label, pricePerLf: 0, priceAdder: opt.priceAdder })}
                        style={{ padding: 14, borderRadius: 10, border: `2px solid ${isSelected ? COLORS.honey : "rgba(155,142,130,0.15)"}`, background: isSelected ? "rgba(196,145,59,0.06)" : COLORS.glassBg, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.bark }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: COLORS.stone, marginTop: 2 }}>{opt.desc}</div>
                        {opt.priceAdder > 0 && <div style={{ fontSize: 12, color: COLORS.honey, fontWeight: 600, marginTop: 4 }}>+${opt.priceAdder}/sq ft</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add-Ons — all trades */}
            <label style={{ ...styles.label, marginTop: activeTrade?.hasRailing ? 8 : 0 }}>
              {activeTrade?.slug === "concrete" ? "Concrete Add-Ons" :
               activeTrade?.slug === "painting" ? "Painting Add-Ons" :
               activeTrade?.slug === "roofing" ? "Roofing Add-Ons" :
               "Deck Add-Ons"}
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {EXTRAS.map(extra => {
                const selected = selectedExtras.find(e => e.id === extra.id);
                return (
                  <button key={extra.id} onClick={() => setSelectedExtras(prev => selected ? prev.filter(e => e.id !== extra.id) : [...prev, extra])} style={{ padding: 14, borderRadius: 10, border: `2px solid ${selected ? COLORS.pine : "rgba(155,142,130,0.15)"}`, background: selected ? "rgba(74,124,89,0.06)" : COLORS.glassBg, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {selected && <Icons.Check />}
                      <span style={{ fontWeight: 600, fontSize: 13, color: COLORS.bark }}>{extra.name}</span>
                    </div>
                    <div style={{ fontSize: 13, color: COLORS.honey, fontWeight: 600, marginTop: 4 }}>+${extra.basePrice.toLocaleString()}</div>
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => { setQuoteStep(1); scrollToQuote(); }} style={styles.secondaryBtn}>← Back</button>
              <button onClick={() => { setQuoteStep(3); scrollToQuote(); }} style={{ ...styles.primaryBtn, flex: 1 }}>Continue to Your Info →</button>
            </div>
          </div>
        )}

        {/* Step 3: Customer Info */}
        {quoteStep === 3 && !quoteSubmitted && (
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Your Information</h3>
            <p style={{ color: COLORS.stone, fontSize: 14, marginBottom: 20 }}>We'll send your personalized estimate and follow up to schedule a free consultation.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={styles.label}>Name</label><input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Your Name" autoComplete="name" style={styles.input} /></div>
              <div><label style={styles.label}>Email</label><input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="you@email.com" autoComplete="email" style={styles.input} /></div>
              <div><label style={styles.label}>Phone</label><input type="tel" value={customerPhone} onChange={e => setCustomerPhone(formatPhone(e.target.value))} placeholder="(530) 555-0000" autoComplete="tel" style={styles.input} /></div>
              <div><label style={styles.label}>Street Address</label><input value={customerAddress} onChange={e => setCustomerAddress(e.target.value)} placeholder="123 Pine St" autoComplete="street-address" style={styles.input} /></div>
              <div><label style={styles.label}>City</label><input value={customerCity} onChange={e => setCustomerCity(e.target.value)} placeholder="Truckee" autoComplete="address-level2" style={styles.input} /></div>
              <div><label style={styles.label}>State / Zip</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={customerState} onChange={e => setCustomerState(e.target.value)} placeholder="CA" autoComplete="address-level1" style={{ ...styles.input, flex: 1 }} />
                  <input value={customerZip} onChange={e => setCustomerZip(e.target.value)} placeholder="96161" autoComplete="postal-code" style={{ ...styles.input, flex: 1 }} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>Project Notes</label>
              <textarea value={customerNotes} onChange={e => setCustomerNotes(e.target.value)} placeholder="Tell us about your project — existing deck condition, goals, timeline, anything helps..." style={styles.textarea} />
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => { setQuoteStep(2); scrollToQuote(); }} style={styles.secondaryBtn}>← Back</button>
              <button
                disabled={quoteSubmitting}
                onClick={async () => {
                  // Validate required fields
                  if (!customerName.trim() || !customerEmail.trim() || !customerPhone.trim()) {
                    setQuoteError("Please fill in your name, email, and phone number.");
                    return;
                  }
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim())) {
                    setQuoteError("Please enter a valid email address.");
                    return;
                  }
                  setQuoteSubmitting(true);
                  setQuoteError(null);
                  trackActivity();
                  try {
                    const fullAddress = [customerAddress, customerCity, customerState, customerZip].filter(Boolean).join(', ');
                    const { data, error } = await submitQuote({
                      market: activeMarket?.slug || null,
                      trade: activeTrade?.slug || null,
                      customerName,
                      customerEmail,
                      customerPhone,
                      customerAddress: fullAddress,
                      customerNotes,
                      deckSqFt,
                      materialId: selectedMaterial?.id || null,
                      materialName: selectedMaterial?.name || null,
                      colorSelection: selectedColor?.name || null,
                      railingType: selectedRailing?.name || null,
                      railingLf: selectedRailing?.id !== "none" ? railingLf : 0,
                      extras: selectedExtras.map(e => ({ id: e.id, name: e.name, price: e.basePrice })),
                      photoUrls: uploadedPhotos.map(p => p.storageUrl).filter(Boolean),
                      estimateLow: Math.round(totalEstimate * 0.9),
                      estimateHigh: Math.round(totalEstimate * 1.1),
                      estimateBreakdown: { materialCost, laborCost, railingCost, extrasCost, overhead, total: totalEstimate },
                    });
                    if (error) throw error;
                    if (data?.id) setQuoteId(data.id);
                    setQuoteSubmitted(true);
                  } catch (err) {
                    console.error("Submit failed:", err);
                    setQuoteError(`Something went wrong. Your quote details are saved — please contact us at ${OWNER_EMAIL}.`);
                  } finally {
                    setQuoteSubmitting(false);
                  }
                }}
                style={{ ...styles.primaryBtn, flex: 1, background: quoteSubmitting ? COLORS.stone : COLORS.pine, cursor: quoteSubmitting ? "wait" : "pointer" }}
              >
                {quoteSubmitting ? "Submitting..." : "Submit Quote Request ✓"}
              </button>
              {quoteError && (
                <div style={{ color: COLORS.red, fontSize: 13, marginTop: 8, textAlign: "center" }}>{quoteError}</div>
              )}
            </div>
          </div>
        )}

        {quoteSubmitted && (
          <div style={{ ...styles.card, textAlign: "center", padding: 48 }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(74,124,89,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", color: COLORS.pine }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h3 style={{ ...styles.sectionTitle, textAlign: "center" }}>Quote Request Submitted!</h3>
            <p style={{ color: COLORS.stone, fontSize: 15, maxWidth: 400, margin: "0 auto" }}>{activeTrade?.submitConfirmation || "Thanks! We'll review your project details and get back to you within 24 hours with a detailed estimate."}</p>
            <div style={{ marginTop: 24, padding: 20, background: COLORS.cream, borderRadius: 12, textAlign: "left", maxWidth: 360, margin: "24px auto 0" }}>
              <div style={{ fontSize: 12, color: COLORS.stone, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Your Estimate Range</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: COLORS.bark, fontFamily: "'Playfair Display', serif" }}>${Math.round(totalEstimate * 0.9).toLocaleString()} — ${Math.round(totalEstimate * 1.1).toLocaleString()}</div>
              <div style={{ fontSize: 13, color: COLORS.stone, marginTop: 4 }}>Final price confirmed after site visit</div>
            </div>

            {/* Book Project — Stripe deposit CTA */}
            {totalEstimate > 0 && (
              <div style={{ maxWidth: 360, margin: "24px auto 0" }}>
                <button
                  onClick={() => {
                    setBookingName(customerName || "");
                    setBookingEmail(customerEmail || "");
                    setBookingPhone(customerPhone || "");
                    setBookingError(null);
                    setShowBookingModal(true);
                  }}
                  style={{
                    width: "100%", padding: "16px 20px", borderRadius: 12, border: "none",
                    background: COLORS.pine, color: COLORS.white, fontSize: 16, fontWeight: 700,
                    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                    boxShadow: "0 4px 16px rgba(74,124,89,0.35)", letterSpacing: 0.3,
                  }}
                >
                  Book This Project — Pay ${Math.round(totalEstimate * 0.165).toLocaleString()} Deposit
                </button>
                <div style={{ fontSize: 12, color: COLORS.stone, textAlign: "center", marginTop: 8 }}>
                  16.5% signing deposit · Secured by Stripe · Fixed-bid contract
                </div>

                {/* Schedule Site Visit — Calendly */}
                <a
                  href={`${CALENDLY_URL}?name=${encodeURIComponent(customerName || "")}&email=${encodeURIComponent(customerEmail || "")}&a1=${encodeURIComponent(customerPhone || "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "block", width: "100%", padding: "14px 20px", borderRadius: 12,
                    border: `2px solid ${COLORS.honey}`, background: "transparent",
                    color: COLORS.honey, fontSize: 15, fontWeight: 700, textAlign: "center",
                    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                    textDecoration: "none", marginTop: 12, letterSpacing: 0.3,
                    boxSizing: "border-box",
                  }}
                >
                  Schedule Free Site Visit
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* RIGHT: Preview + Estimate + AI Chat */}
      <div className="quote-grid-right" style={{ display: "flex", flexDirection: "column", gap: 16, position: "sticky", top: 88 }}>
        {/* Live Trade Preview */}
        <TradePreview trade={activeTrade} market={activeMarket} sqft={deckSqFt} material={selectedMaterial} railing={selectedRailing} extras={selectedExtras} color={selectedColor} />

        {/* Proactive Tips — market + trade aware */}
        {(() => {
          const tips = [];
          const marketName = activeMarket?.displayName || "";
          const tradeSlug = activeTrade?.slug || "decks";
          const codeNote = activeMarket?.buildingCodes?.[tradeSlug];

          // Local code tip from market config
          if (codeNote) tips.push(codeNote);

          // Material tips
          if (selectedMaterial?.id === "ipe") tips.push("Ipê is incredibly dense — needs pre-drilling for every fastener. Worth it for 40+ year lifespan.");
          if (selectedMaterial?.id === "cedar-decking" || selectedMaterial?.id === "redwood") tips.push("Natural wood needs sealing every 2-3 years. Budget $1-2/sqft for long-term maintenance.");
          if (selectedMaterial?.id === "pressure-treated") tips.push("Pressure-treated lumber is the most affordable option and holds up well — plan for staining or sealing within the first year.");
          // Railing tips
          if (selectedRailing?.id === "glass") tips.push("Glass panels give unobstructed views but need regular cleaning. Budget for seasonal maintenance.");
          if (selectedRailing?.id === "cable") tips.push("Cable railing preserves views and has a clean modern look. Popular for elevated decks and properties with scenic surroundings.");
          // Extras tips
          if (selectedExtras.some(e => e.id === "hottub")) tips.push("Hot tub reinforcement requires additional structural engineering. We'll size the footings correctly on site.");
          if (selectedExtras.some(e => e.id === "pergola")) tips.push("Pergolas need proper post sizing for wind and snow loads in your area. We'll engineer it correctly.");
          if (selectedExtras.some(e => e.id === "lighting")) tips.push("LED deck lighting dramatically extends how much you use your outdoor space. Low-voltage and waterproof-rated for longevity.");
          if (selectedExtras.some(e => e.id === "tearout")) tips.push("Concrete removal costs vary by thickness and access. We'll confirm pricing on site visit.");
          // Size tips
          if (deckSqFt > 800) tips.push("Large project — we'll discuss phasing options to fit your timeline and budget.");
          if (deckSqFt < 200) tips.push("Compact footprint — great for maximizing a small space. Built-in features can maximize every square foot.");

          if (!tips.length) return null;
          return (
            <div style={{ ...styles.card, padding: 14, background: "rgba(74,124,89,0.04)", border: `1px solid rgba(74,124,89,0.15)` }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: COLORS.pine, marginBottom: 6 }}>
                {codeNote ? `${marketName} Code Tip` : "Pro Tip"}
              </div>
              <div style={{ fontSize: 12, color: COLORS.bark, lineHeight: 1.5 }}>{tips[tips.length - 1]}</div>
              {tips.length > 1 && (
                <div style={{ fontSize: 11, color: COLORS.stone, marginTop: 4 }}>+{tips.length - 1} more tip{tips.length > 2 ? "s" : ""} based on your selections</div>
              )}
            </div>
          );
        })()}

        {/* Live Estimate */}
        <div style={{ ...styles.card, background: COLORS.bark, color: COLORS.snow }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 2, opacity: 0.5, marginBottom: 8 }}>Live Estimate</div>
          <div style={{ fontSize: 36, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.honey }}>
            $<AnimatedNumber value={Math.round(totalEstimate)} />
          </div>
          <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 16 }}>{deckSqFt} sq ft · Fixed bid pricing</div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.6 }}>Materials</span>
              <span>${materialCost.toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.6 }}>Labor</span>
              <span>${laborCost.toLocaleString()}</span>
            </div>
            {railingCost > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.6 }}>Railing</span>
              <span>${railingCost.toLocaleString()}</span>
            </div>}
            {extrasCost > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.6 }}>Add-ons</span>
              <span>${extrasCost.toLocaleString()}</span>
            </div>}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.6 }}>Overhead & Profit</span>
              <span>${Math.round(overhead).toLocaleString()}</span>
            </div>
          </div>

          {selectedMaterial && (
            <div style={{ marginTop: 12, padding: 10, background: "rgba(255,255,255,0.06)", borderRadius: 8, fontSize: 12 }}>
              <strong>{selectedMaterial.name}</strong>{selectedColor ? ` in ${selectedColor.name}` : ""}
              {selectedRailing && selectedRailing.id !== "none" && <span> · {selectedRailing.name}</span>}
              {selectedExtras.length > 0 && <span> · +{selectedExtras.length} add-on{selectedExtras.length > 1 ? "s" : ""}</span>}
            </div>
          )}
        </div>

        {/* Book This Project — Stripe deposit CTA (only after quote submitted) */}
        {quoteSubmitted && totalEstimate > 0 && (
          <div style={{ ...styles.card, background: "linear-gradient(135deg, rgba(196,145,59,0.08) 0%, rgba(74,124,89,0.06) 100%)", border: `1px solid rgba(196,145,59,0.25)` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.bark, marginBottom: 4 }}>
              Ready to lock in your project?
            </div>
            <div style={{ fontSize: 12, color: COLORS.stone, marginBottom: 14, lineHeight: 1.5 }}>
              Secure your spot with a 16.5% deposit —{" "}
              <strong style={{ color: COLORS.honey }}>
                ${Math.round(totalEstimate * 0.165).toLocaleString()}
              </strong>
              . Fixed-bid contract means no overruns.
            </div>
            <button
              onClick={() => {
                setBookingName(customerName || "");
                setBookingEmail(customerEmail || "");
                setBookingPhone(customerPhone || "");
                setBookingError(null);
                setShowBookingModal(true);
              }}
              style={{
                width: "100%",
                padding: "13px 20px",
                borderRadius: 10,
                border: "none",
                background: COLORS.pine,
                color: COLORS.white,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 3px 12px rgba(74,124,89,0.3)",
                letterSpacing: 0.3,
              }}
            >
              Book This Project — Pay Deposit
            </button>
            <div style={{ fontSize: 11, color: COLORS.stone, textAlign: "center", marginTop: 8 }}>
              Secured by Stripe · Cancel before site visit for full refund
            </div>
          </div>
        )}

        {/* AI Chat */}
        <div style={{ ...styles.card, padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", height: 380 }}>
          <div style={{ padding: "14px 20px", background: COLORS.pine, color: COLORS.white, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            <Icons.Mountain />
            <div>
              <div>UltraLife Trades AI Assistant</div>
              <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 400 }}>{activeTrade?.chatPlaceholder || "Ask about materials, costs, timelines..."}</div>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            {chatMessages.map((msg, i) => (
              <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{ maxWidth: "85%", padding: "10px 14px", borderRadius: 14, fontSize: 13, lineHeight: 1.5, background: msg.role === "user" ? COLORS.honey : COLORS.cream, color: msg.role === "user" ? COLORS.white : COLORS.bark, borderBottomRightRadius: msg.role === "user" ? 4 : 14, borderBottomLeftRadius: msg.role === "user" ? 14 : 4 }}>
                  {msg.text}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div style={{ display: "flex", gap: 4, padding: "10px 14px" }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS.stone, opacity: 0.4, animation: `pulse 1s ease-in-out ${i * 0.15}s infinite` }} />
                ))}
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div style={{ padding: 12, borderTop: "1px solid rgba(155,142,130,0.12)", display: "flex", gap: 8 }}>
            <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendChat()} placeholder={activeTrade?.chatPlaceholder || "Ask anything about your project..."} style={{ ...styles.input, background: COLORS.white, flex: 1 }} />
            <button onClick={sendChat} style={{ width: 40, height: 40, borderRadius: 10, border: "none", background: COLORS.pine, color: COLORS.white, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icons.Send />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── RENDER CRM ───
  const renderCRM = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={styles.sectionTitle}>Customer Management</h2>
        <div style={{ fontSize: 12, color: COLORS.stone, fontWeight: 600 }}>
          Admin scope: <span style={{ color: COLORS.honey }}>All markets</span>
          {crmMarketsPresent.length > 0 && (
            <span style={{ marginLeft: 6, opacity: 0.7 }}>
              · {crmMarketsPresent.length} market{crmMarketsPresent.length === 1 ? "" : "s"} with leads
            </span>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {["All", "Lead", "Estimate Sent", "Active Project", "Completed"].map(filter => (
          <button key={filter} onClick={() => setCrmFilter(filter)} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${crmFilter === filter ? COLORS.honey : "rgba(155,142,130,0.2)"}`, background: crmFilter === filter ? "rgba(196,145,59,0.08)" : COLORS.white, color: crmFilter === filter ? COLORS.honey : COLORS.stone, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
            {filter}
          </button>
        ))}
      </div>

      {/* Market filter — only show if quotes from multiple markets exist */}
      {crmMarketsPresent.length > 1 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: COLORS.stone, marginRight: 4 }}>Market:</span>
          <button onClick={() => setCrmMarketFilter("All")} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${crmMarketFilter === "All" ? COLORS.honey : "rgba(155,142,130,0.2)"}`, background: crmMarketFilter === "All" ? "rgba(196,145,59,0.08)" : COLORS.white, color: crmMarketFilter === "All" ? COLORS.honey : COLORS.stone, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
            All ({crmCustomers.length})
          </button>
          {crmMarketsPresent.map(slug => {
            const m = getMarket(slug);
            const count = crmCustomers.filter(c => c.market_slug === slug).length;
            const label = m ? `${m.displayName}, ${m.state}` : slug;
            return (
              <button key={slug} onClick={() => setCrmMarketFilter(slug)} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${crmMarketFilter === slug ? COLORS.honey : "rgba(155,142,130,0.2)"}`, background: crmMarketFilter === slug ? "rgba(196,145,59,0.08)" : COLORS.white, color: crmMarketFilter === slug ? COLORS.honey : COLORS.stone, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                {label} ({count})
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: selectedCustomer ? "1fr 400px" : "1fr", gap: 20 }}>
        <div style={styles.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid rgba(155,142,130,0.1)" }}>
                {["Customer", "Market", "Project", "Status", "Estimate", "Balance"].map(h => (
                  <th key={h} style={{ ...styles.label, padding: "8px 12px", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => {
                const cm = customer.market_slug ? getMarket(customer.market_slug) : null;
                return (
                <tr key={customer.id} onClick={() => setSelectedCustomer(customer)} style={{ borderBottom: "1px solid rgba(155,142,130,0.08)", cursor: "pointer", background: selectedCustomer?.id === customer.id ? "rgba(196,145,59,0.04)" : "transparent", transition: "background 0.15s" }}>
                  <td style={{ padding: "14px 12px" }}>
                    <div style={{ fontWeight: 600, color: COLORS.bark, fontSize: 14 }}>{customer.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.stone }}>{customer.address}</div>
                  </td>
                  <td style={{ padding: "14px 12px", fontSize: 12 }}>
                    {cm ? (
                      <>
                        <div style={{ fontWeight: 600, color: COLORS.bark }}>{cm.displayName}</div>
                        <div style={{ fontSize: 11, color: COLORS.stone }}>
                          {cm.state}{customer.trade_slug ? ` · ${customer.trade_slug}` : ""}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: COLORS.stone, fontStyle: "italic" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "14px 12px", fontSize: 13 }}>{customer.project}</td>
                  <td style={{ padding: "14px 12px" }}><StatusBadge status={customer.status} /></td>
                  <td style={{ padding: "14px 12px", fontWeight: 600, fontSize: 14 }}>{customer.estimate > 0 ? `$${customer.estimate.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: "14px 12px", fontWeight: 600, fontSize: 14, color: customer.estimate - customer.paid > 0 ? COLORS.red : COLORS.green }}>
                    {customer.estimate > 0 ? `$${(customer.estimate - customer.paid).toLocaleString()}` : "—"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selectedCustomer && (
          <div style={styles.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 16 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.bark, fontFamily: "'Playfair Display', serif" }}>{selectedCustomer.name}</h3>
                <StatusBadge status={selectedCustomer.status} />
              </div>
              <button onClick={() => setSelectedCustomer(null)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.stone }}><Icons.X /></button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 13 }}>
              <div><span style={{ ...styles.label, display: "block" }}>Contact</span>{selectedCustomer.email || "No email"}<br />{selectedCustomer.phone || "No phone"}</div>
              <div><span style={{ ...styles.label, display: "block" }}>Address</span>{selectedCustomer.address || "Not provided"}</div>
              <div>
                <span style={{ ...styles.label, display: "block" }}>Market / Trade</span>
                {(() => {
                  const m = selectedCustomer.market_slug ? getMarket(selectedCustomer.market_slug) : null;
                  if (!m && !selectedCustomer.trade_slug) return <span style={{ color: COLORS.stone, fontStyle: "italic" }}>Not specified</span>;
                  return `${m ? `${m.displayName}, ${m.state}` : "Unknown market"}${selectedCustomer.trade_slug ? ` · ${selectedCustomer.trade_slug}` : ""}`;
                })()}
              </div>
              <div><span style={{ ...styles.label, display: "block" }}>Project</span>{selectedCustomer.project}{selectedCustomer.sqft > 0 ? ` · ${selectedCustomer.sqft} sq ft` : ""}{selectedCustomer.material && selectedCustomer.material !== "TBD" ? ` · ${selectedCustomer.material}` : ""}</div>
              {selectedCustomer.notes && <div><span style={{ ...styles.label, display: "block" }}>Notes</span>{selectedCustomer.notes}</div>}

              {/* Estimate details for leads */}
              {selectedCustomer.estimate > 0 && (
                <div style={{ padding: 16, background: COLORS.cream, borderRadius: 10, marginTop: 4 }}>
                  <span style={styles.label}>Estimate Range</span>
                  <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.bark, fontFamily: "'Playfair Display', serif", marginTop: 4 }}>
                    ${Math.round(selectedCustomer.estimate * 0.9).toLocaleString()} — ${selectedCustomer.estimate.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.stone, marginTop: 2 }}>From quote builder · Confirm after site visit</div>
                  {selectedCustomer.paid > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(155,142,130,0.15)" }}>
                      <div><div style={{ fontSize: 11, color: COLORS.stone }}>Paid</div><div style={{ fontSize: 16, fontWeight: 700, color: COLORS.green }}>${selectedCustomer.paid.toLocaleString()}</div></div>
                      <div><div style={{ fontSize: 11, color: COLORS.stone }}>Balance</div><div style={{ fontSize: 16, fontWeight: 700, color: COLORS.red }}>${(selectedCustomer.estimate - selectedCustomer.paid).toLocaleString()}</div></div>
                    </div>
                  )}
                </div>
              )}

              {/* ─── LIFECYCLE ACTIONS ─── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>

                {/* LEAD: Primary action is Send Estimate */}
                {selectedCustomer.status === "Lead" && (
                  <>
                    <button
                      onClick={() => setShowEstimateModal(true)}
                      style={{ ...styles.primaryBtn, width: "100%", fontSize: 13, padding: "12px", background: COLORS.honey }}
                    >
                      <Icons.Send /> Send Estimate
                    </button>
                    <div style={{ display: "flex", gap: 8 }}>
                      {selectedCustomer.email && (
                        <a
                          href={`mailto:${selectedCustomer.email}?subject=Site Visit — UltraLife Trades&body=Hi ${(selectedCustomer.name || '').split(' ')[0]},%0A%0AI'd love to come by and take a look at your deck project. Are you available this week for a quick site visit?%0A%0ACheers,%0AUltraLife Trades`}
                          style={{ ...styles.secondaryBtn, flex: 1, fontSize: 12, padding: "10px 12px", textDecoration: "none", textAlign: "center" }}
                        >Schedule Visit</a>
                      )}
                      {selectedCustomer.phone && (
                        <a href={`tel:${selectedCustomer.phone}`} style={{ ...styles.secondaryBtn, flex: 1, fontSize: 12, padding: "10px 12px", textDecoration: "none", textAlign: "center" }}>
                          Call
                        </a>
                      )}
                    </div>
                  </>
                )}

                {/* ESTIMATE SENT: Primary action is Create Invoice (deposit) */}
                {selectedCustomer.status === "Estimate Sent" && (
                  <>
                    <button
                      onClick={() => {
                        const deposit = Math.round(selectedCustomer.estimate * 0.165);
                        setInvoiceForCustomer(selectedCustomer);
                        setInvoiceForm({
                          customerName: selectedCustomer.name,
                          customerEmail: selectedCustomer.email || '',
                          customerAddress: selectedCustomer.address || '',
                          description: `16.5% deposit — ${selectedCustomer.project}`,
                          lineItems: [{ description: `16.5% deposit — ${selectedCustomer.project}`, amount: String(deposit) }],
                          dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        });
                        setShowInvoiceModal(true);
                        switchView("invoices");
                      }}
                      style={{ ...styles.primaryBtn, width: "100%", fontSize: 13, padding: "12px" }}
                    >
                      <Icons.Dollar /> Create Deposit Invoice
                    </button>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setShowEstimateModal(true)} style={{ ...styles.secondaryBtn, flex: 1, fontSize: 12, padding: "10px 12px" }}>Resend Estimate</button>
                      {selectedCustomer.phone && (
                        <a href={`tel:${selectedCustomer.phone}`} style={{ ...styles.secondaryBtn, flex: 1, fontSize: 12, padding: "10px 12px", textDecoration: "none", textAlign: "center" }}>Call</a>
                      )}
                    </div>
                  </>
                )}

                {/* ACTIVE PROJECT: Send contract + Create invoice */}
                {selectedCustomer.status === "Active Project" && (
                  <>
                    {selectedCustomer.email && (
                      <button
                        onClick={async () => {
                          const result = await sendContract({
                            customerName: selectedCustomer.name,
                            customerEmail: selectedCustomer.email,
                            customerPhone: selectedCustomer.phone,
                            customerAddress: selectedCustomer.address,
                            trade: activeTrade?.displayName || '',
                            market: activeMarket?.displayName || '',
                            sqft: selectedCustomer.sqft || 0,
                            material: selectedCustomer.material || '',
                            extras: [],
                            totalEstimate: selectedCustomer.estimate || 0,
                            projectId: selectedCustomer.dbType === 'project' ? selectedCustomer.id : null,
                          });
                          if (result?.success) {
                            alert(`Contract sent to ${selectedCustomer.email} for signature!`);
                          } else {
                            alert('Failed to send contract. Check DocuSign configuration.');
                          }
                        }}
                        style={{ ...styles.primaryBtn, width: "100%", fontSize: 13, padding: "12px", background: COLORS.bark }}
                      >
                        <Icons.Invoice /> Send Contract (DocuSign)
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setInvoiceForCustomer(selectedCustomer);
                        setInvoiceForm({
                          customerName: selectedCustomer.name,
                          customerEmail: selectedCustomer.email || '',
                          customerAddress: selectedCustomer.address || '',
                          description: '',
                          lineItems: [{ description: '', amount: '' }],
                          dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        });
                        setShowInvoiceModal(true);
                        switchView("invoices");
                      }}
                      style={{ ...styles.primaryBtn, width: "100%", fontSize: 13, padding: "12px" }}
                    >
                      <Icons.Invoice /> Create Invoice
                    </button>
                    <div style={{ display: "flex", gap: 8 }}>
                      {selectedCustomer.email && (
                        <a href={`mailto:${selectedCustomer.email}?subject=Project Update — UltraLife Trades`} style={{ ...styles.secondaryBtn, flex: 1, fontSize: 12, padding: "10px 12px", textDecoration: "none", textAlign: "center" }}>Send Update</a>
                      )}
                      {selectedCustomer.phone && (
                        <a href={`tel:${selectedCustomer.phone}`} style={{ ...styles.secondaryBtn, flex: 1, fontSize: 12, padding: "10px 12px", textDecoration: "none", textAlign: "center" }}>Call</a>
                      )}
                    </div>
                  </>
                )}

                {/* COMPLETED: Final invoice or email */}
                {selectedCustomer.status === "Completed" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => {
                        const balance = selectedCustomer.estimate - selectedCustomer.paid;
                        setInvoiceForCustomer(selectedCustomer);
                        setInvoiceForm({
                          customerName: selectedCustomer.name,
                          customerEmail: selectedCustomer.email || '',
                          customerAddress: selectedCustomer.address || '',
                          description: `Final payment — ${selectedCustomer.project}`,
                          lineItems: [{ description: `Final payment — ${selectedCustomer.project}`, amount: String(balance > 0 ? balance : '') }],
                          dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        });
                        setShowInvoiceModal(true);
                        switchView("invoices");
                      }}
                      style={{ ...styles.primaryBtn, flex: 1, fontSize: 12, padding: "10px 12px" }}
                    >Final Invoice</button>
                    {selectedCustomer.email && (
                      <a href={`mailto:${selectedCustomer.email}?subject=Thank you — UltraLife Trades`} style={{ ...styles.secondaryBtn, flex: 1, fontSize: 12, padding: "10px 12px", textDecoration: "none", textAlign: "center" }}>Send Email</a>
                    )}
                  </div>
                )}

                {/* DANGER ZONE: Delete this customer/quote */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(155,142,130,0.15)" }}>
                  <button
                    onClick={async () => {
                      const label = selectedCustomer.dbType === "project" ? "project" : "quote";
                      const confirmMsg = `Delete this ${label} for ${selectedCustomer.name}?\n\nThis cannot be undone. ${selectedCustomer.dbType === "project" ? "All linked invoices, receipts, and inspections may be removed depending on cascade rules." : ""}`;
                      // eslint-disable-next-line no-alert
                      if (!window.confirm(confirmMsg)) return;
                      const { error } = selectedCustomer.dbType === "project"
                        ? await deleteProject(selectedCustomer.id)
                        : await deleteQuote(selectedCustomer.id);
                      if (error) {
                        // eslint-disable-next-line no-alert
                        window.alert(`Could not delete: ${error}`);
                        return;
                      }
                      // Optimistically remove from local state, then refresh from DB.
                      if (selectedCustomer.dbType === "project") {
                        setLiveProjects(prev => prev.filter(p => p.id !== selectedCustomer.id));
                      } else {
                        setLiveQuotes(prev => prev.filter(q => q.id !== selectedCustomer.id));
                      }
                      setSelectedCustomer(null);
                      loadData();
                    }}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: `1px solid ${COLORS.red}`,
                      background: "transparent",
                      color: COLORS.red,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    Delete {selectedCustomer.dbType === "project" ? "project" : "quote"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── SEND ESTIMATE MODAL ─── */}
        {showEstimateModal && selectedCustomer && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowEstimateModal(false)}>
            <div onClick={e => e.stopPropagation()} style={{ background: COLORS.white, borderRadius: 16, padding: 32, width: 540, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.15)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h3 style={styles.sectionTitle}>Send Estimate</h3>
                <button onClick={() => setShowEstimateModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.stone }}><Icons.X /></button>
              </div>

              {/* Customer info */}
              <div style={{ padding: 16, background: COLORS.cream, borderRadius: 10, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: COLORS.bark, fontSize: 15 }}>{selectedCustomer.name}</div>
                <div style={{ fontSize: 13, color: COLORS.stone }}>{selectedCustomer.email} · {selectedCustomer.phone}</div>
                <div style={{ fontSize: 13, color: COLORS.stone }}>{selectedCustomer.address}</div>
              </div>

              {/* Project details */}
              <div style={{ padding: 16, background: COLORS.cream, borderRadius: 10, marginBottom: 16 }}>
                <div style={styles.label}>Project Details</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                  <div><span style={{ fontSize: 11, color: COLORS.stone }}>Size</span><div style={{ fontWeight: 600 }}>{selectedCustomer.sqft || "TBD"} sq ft</div></div>
                  <div><span style={{ fontSize: 11, color: COLORS.stone }}>Material</span><div style={{ fontWeight: 600 }}>{selectedCustomer.material || "TBD"}</div></div>
                </div>
                {selectedCustomer.notes && <div style={{ marginTop: 8, fontSize: 12, color: COLORS.stone }}>{selectedCustomer.notes}</div>}
              </div>

              {/* Estimate range */}
              <div style={{ padding: 20, background: COLORS.bark, borderRadius: 12, color: COLORS.snow, marginBottom: 16, textAlign: "center" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 2, opacity: 0.5, marginBottom: 4 }}>Estimate Range</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: COLORS.honey, fontFamily: "'Playfair Display', serif" }}>
                  ${Math.round((selectedCustomer.estimate || 0) * 0.9).toLocaleString()} — ${(selectedCustomer.estimate || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 12, opacity: 0.5, marginTop: 4 }}>Fixed bid · Final price confirmed after site visit</div>
              </div>

              {/* Email preview */}
              <div style={{ padding: 16, border: "1px solid rgba(155,142,130,0.2)", borderRadius: 10, marginBottom: 16, fontSize: 13, color: COLORS.slate, lineHeight: 1.6 }}>
                <div style={{ ...styles.label, marginBottom: 8 }}>Email Preview</div>
                <p>Hi {(selectedCustomer.name || '').split(' ')[0]},</p>
                <p>Thanks for your interest in UltraLife Trades! Based on your {selectedCustomer.sqft || ""}sqft {selectedCustomer.material || "project"}, here's my estimate:</p>
                <p style={{ fontWeight: 700, fontSize: 15 }}>${Math.round((selectedCustomer.estimate || 0) * 0.9).toLocaleString()} — ${(selectedCustomer.estimate || 0).toLocaleString()}</p>
                <p>This is a fixed-bid range. I'll confirm exact pricing after a site visit. This estimate does not include engineering plans or permits.</p>
                <p>I'd love to come by and take a look — let me know what works for your schedule.</p>
                <p>— {OWNER_NAME}<br/>{OWNER_PHONE && `${OWNER_PHONE} · `}{OWNER_WEBSITE}</p>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setShowEstimateModal(false)} style={styles.secondaryBtn}>Cancel</button>
                {selectedCustomer.email ? (
                  <button
                    disabled={estimateSending}
                    onClick={async () => {
                      setEstimateSending(true);

                      // Generate branded estimate document
                      const doc = await generateDocument("estimate", {
                        customerName: selectedCustomer.name,
                        customerEmail: selectedCustomer.email,
                        customerPhone: selectedCustomer.phone,
                        customerAddress: selectedCustomer.address,
                        projectDescription: selectedCustomer.notes,
                        sqft: selectedCustomer.sqft,
                        material: selectedCustomer.material,
                        estimateLow: Math.round((selectedCustomer.estimate || 0) * 0.9),
                        estimateHigh: selectedCustomer.estimate || 0,
                      });

                      const docLink = doc?.url ? `\n\nView your full estimate: ${doc.url}` : "";

                      // Open email client with estimate
                      const subject = encodeURIComponent(`Your Estimate — ${selectedCustomer.project} · UltraLife Trades`);
                      const body = encodeURIComponent(
`Hi ${(selectedCustomer.name || '').split(' ')[0]},

Thanks for your interest in UltraLife Trades! Based on your ${selectedCustomer.sqft ? `${selectedCustomer.sqft}sqft ` : ""}${selectedCustomer.material || "project"}, here's my estimate:

$${Math.round((selectedCustomer.estimate || 0) * 0.9).toLocaleString()} — $${(selectedCustomer.estimate || 0).toLocaleString()}

This is a fixed-bid range — the estimate IS the price. I'll confirm exact pricing after a site visit. This does not include permits or site-specific engineering.${docLink}

I'd love to come by and take a look — let me know what works for your schedule.

${OWNER_NAME}
${OWNER_PHONE ? `${OWNER_PHONE} · ` : ""}${OWNER_EMAIL}
${OWNER_WEBSITE}`
                      );
                      window.open(`mailto:${selectedCustomer.email}?subject=${subject}&body=${body}`, '_self');

                      // Update quote status to bid_sent
                      if (selectedCustomer.dbType === "quote") {
                        await updateQuoteStatus(selectedCustomer.id, 'bid_sent');
                        setLiveQuotes(prev => prev.map(q => q.id === selectedCustomer.id ? { ...q, status: 'bid_sent' } : q));
                        setSelectedCustomer({ ...selectedCustomer, status: "Estimate Sent" });
                      }
                      setEstimateSending(false);
                      setShowEstimateModal(false);
                    }}
                    style={{ ...styles.primaryBtn, flex: 1, background: COLORS.honey }}
                  >
                    {estimateSending ? "Generating..." : "Generate Estimate & Email"}
                  </button>
                ) : (
                  <div style={{ flex: 1, padding: "12px", textAlign: "center", color: COLORS.red, fontSize: 13, fontWeight: 600, background: "rgba(196,75,59,0.06)", borderRadius: 10 }}>
                    No email on file — call {selectedCustomer.phone || "customer"} to send verbally
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── INVOICE HELPERS ───
  const handleCreateInvoice = async () => {
    setInvoiceCreating(true);
    const lines = invoiceForm.lineItems.filter(l => l.description && l.amount);
    const subtotal = lines.reduce((s, l) => s + Number(l.amount), 0);
    // Use timestamp-based suffix to avoid collision if two invoices created simultaneously
    const invNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;

    // Default due date: Net 15 from today if not set
    const dueDate = invoiceForm.dueDate || new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const created = await createInvoice({
      invoice_number: invNumber,
      customer_name: invoiceForm.customerName,
      customer_email: invoiceForm.customerEmail,
      customer_address: invoiceForm.customerAddress,
      description: invoiceForm.description,
      line_items: lines,
      subtotal,
      tax: 0,
      total: subtotal,
      due_date: dueDate,
      status: 'draft',
    });
    if (created) {
      setLiveInvoices(prev => [created, ...prev]);
      setShowInvoiceModal(false);
      setInvoiceForm({ customerName: '', customerEmail: '', customerAddress: '', description: '', lineItems: [{ description: '', amount: '' }], dueDate: '' });

      // Transition customer status when invoice is created
      if (invoiceForCustomer) {
        const cust = invoiceForCustomer;
        // Estimate Sent + deposit invoice → Active Project (won)
        if (cust.status === "Estimate Sent" && cust.dbType === "quote") {
          await updateQuoteStatus(cust.id, 'won');
          setLiveQuotes(prev => prev.map(q => q.id === cust.id ? { ...q, status: 'won' } : q));
          setSelectedCustomer(prev => prev?.id === cust.id ? { ...prev, status: "Active Project" } : prev);
        }
        setInvoiceForCustomer(null);
      }
    }
    setInvoiceCreating(false);
  };

  const handleSendInvoice = async (inv) => {
    setSendingInvoiceId(inv.id);
    const result = await sendInvoice(inv.id);
    if (result) {
      setLiveInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'sent', sent_at: new Date().toISOString() } : i));
    }
    setSendingInvoiceId(null);
  };

  const handleMarkPaid = async (inv) => {
    setPaymentSaving(true);
    let proofUrl = null;
    if (paymentForm.proofFile) {
      const path = `payment-proofs/${Date.now()}_${paymentForm.proofFile.name}`;
      proofUrl = await uploadFile('documents', path, paymentForm.proofFile);
    }
    const updated = await markInvoicePaid(inv.id, {
      paymentMethod: paymentForm.method,
      paidAmount: Number(inv.total),
      paymentProofUrl: proofUrl,
    });
    if (updated) {
      setLiveInvoices(prev => prev.map(i => i.id === inv.id ? updated : i));
    }
    setPayingInvoice(null);
    setPaymentForm({ method: 'check', proofFile: null });
    setPaymentSaving(false);
  };

  // ─── RENDER INVOICES ───
  const renderInvoices = () => {
    const invoiceData = liveInvoices.length > 0
      ? liveInvoices
      : SAMPLE_INVOICES.map(inv => ({ ...inv, id: inv.id, invoice_number: inv.id, customer_name: inv.customer, total: inv.amount, customer_email: null }));

    const totalOutstanding = invoiceData.filter(i => (i.status === "sent" || i.status === "Pending" || i.status === "overdue" || i.status === "Overdue")).reduce((s, i) => s + Number(i.total || i.amount || 0), 0);
    const totalPaid = invoiceData.filter(i => (i.status === "paid" || i.status === "Paid")).reduce((s, i) => s + Number(i.total || i.amount || 0), 0);

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={styles.sectionTitle}>Invoicing</h2>
          <button onClick={() => setShowInvoiceModal(true)} style={styles.primaryBtn}><Icons.Plus /> New Invoice</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
          {[
            { label: "Outstanding", value: totalOutstanding, color: COLORS.honey },
            { label: "Paid (2026)", value: totalPaid, color: COLORS.green },
            { label: "Total Revenue", value: totalOutstanding + totalPaid, color: COLORS.bark },
          ].map((stat, i) => (
            <div key={i} style={{ ...styles.card, textAlign: "center" }}>
              <div style={styles.label}>{stat.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: stat.color, fontFamily: "'Playfair Display', serif" }}>
                $<AnimatedNumber value={stat.value} />
              </div>
            </div>
          ))}
        </div>

        <div style={styles.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid rgba(155,142,130,0.1)" }}>
                {["Invoice #", "Customer", "Description", "Amount", "Status", "Due Date", "Actions"].map(h => (
                  <th key={h} style={{ ...styles.label, padding: "8px 12px", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoiceData.map(inv => {
                const invId = inv.invoice_number || inv.id;
                const invStatus = inv.status === "paid" ? "Paid" : inv.status === "sent" ? "Pending" : inv.status === "overdue" ? "Overdue" : inv.status === "Paid" ? "Paid" : inv.status === "Pending" ? "Pending" : "Draft";
                const amount = Number(inv.total || inv.amount || 0);
                return (
                  <tr key={inv.id} style={{ borderBottom: "1px solid rgba(155,142,130,0.08)" }}>
                    <td style={{ padding: "14px 12px", fontWeight: 600, color: COLORS.blue, fontSize: 13 }}>{invId}</td>
                    <td style={{ padding: "14px 12px", fontSize: 14, fontWeight: 600, color: COLORS.bark }}>{inv.customer_name || inv.customer}</td>
                    <td style={{ padding: "14px 12px", fontSize: 13, color: COLORS.stone }}>{inv.description}</td>
                    <td style={{ padding: "14px 12px", fontSize: 15, fontWeight: 700 }}>${amount.toLocaleString()}</td>
                    <td style={{ padding: "14px 12px" }}><StatusBadge status={invStatus} /></td>
                    <td style={{ padding: "14px 12px", fontSize: 13, color: COLORS.stone }}>{inv.due_date || inv.due || ""}</td>
                    <td style={{ padding: "14px 12px" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        {(invStatus === "Draft" || invStatus === "Pending") && (
                          <button
                            onClick={() => {
                              if (!inv.customer_email) {
                                alert("No customer email on this invoice. Add an email to send.");
                                return;
                              }
                              handleSendInvoice(inv);
                            }}
                            disabled={sendingInvoiceId === inv.id}
                            style={{ padding: "5px 10px", borderRadius: 6, border: "none", background: COLORS.pine, color: COLORS.white, fontSize: 11, fontWeight: 600, cursor: sendingInvoiceId === inv.id ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif" }}
                          >
                            {sendingInvoiceId === inv.id ? "Sending..." : invStatus === "Pending" ? "Resend" : "Send"}
                          </button>
                        )}
                        {(invStatus === "Pending" || invStatus === "Draft") && (
                          <button
                            onClick={() => setPayingInvoice(inv)}
                            style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${COLORS.green}`, background: "transparent", color: COLORS.green, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
                          >
                            Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {invoiceData.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: COLORS.stone }}>No invoices yet. Create one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ─── CREATE INVOICE MODAL ─── */}
        <CreateInvoiceModal
          show={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          onCreate={handleCreateInvoice}
          creating={invoiceCreating}
          invoiceForm={invoiceForm}
          setInvoiceForm={setInvoiceForm}
          invoiceForCustomer={invoiceForCustomer}
          liveInvoices={liveInvoices}
          styles={styles}
        />

        {/* ─── MARK AS PAID MODAL ─── */}
        <MarkPaidModal
          invoice={payingInvoice}
          onClose={() => setPayingInvoice(null)}
          onConfirm={handleMarkPaid}
          saving={paymentSaving}
          paymentForm={paymentForm}
          setPaymentForm={setPaymentForm}
          styles={styles}
        />
      </div>
    );
  };

  // ─── EMPLOYEE DATA ───
  const [employees, setEmployees] = useState([]);
  const [timecards, setTimecards] = useState([]);
  const [timeInput, setTimeInput] = useState("");
  const [timeInputEmployee, setTimeInputEmployee] = useState("");
  const [timeInputPhotos, setTimeInputPhotos] = useState([]);
  const [parsingTime, setParsingTime] = useState(false);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmpForm, setNewEmpForm] = useState({ name: "", phone: "", email: "", hourly_rate: "" });
  const timePhotoRef = useRef(null);

  // Load employee data when switching to team portal
  useEffect(() => {
    if (isAdmin && currentView === "employees") {
      fetchEmployees().then(setEmployees);
      fetchTimecards().then(setTimecards);
    }
  }, [isAdmin, currentView]);

  const handleTimeEntry = async () => {
    if (!timeInput.trim() || !timeInputEmployee) return;
    setParsingTime(true);

    // Upload photos first if any
    const photoUrls = [];
    for (const photo of timeInputPhotos) {
      const path = `timecard-photos/${Date.now()}_${photo.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const url = await uploadFile('photos', path, photo.file);
      if (url) photoUrls.push(url);
    }

    const result = await parseTimecardFromText(timeInput, timeInputEmployee);
    if (result?.success && result.timecard) {
      // Attach photos to the timecard
      if (photoUrls.length > 0 && supabase) {
        await supabase.from('timecards').update({ photo_urls: photoUrls }).eq('id', result.timecard.id);
        result.timecard.photo_urls = photoUrls;
      }
      setTimecards(prev => [result.timecard, ...prev]);
      setTimeInput("");
      setTimeInputPhotos([]);
    }
    setParsingTime(false);
  };

  const handleTimePhotoAdd = (e) => {
    const files = Array.from(e.target.files);
    setTimeInputPhotos(prev => [...prev, ...files.map(f => ({ name: f.name, file: f, preview: URL.createObjectURL(f) }))]);
    e.target.value = "";
  };

  const handleAddEmployee = async () => {
    const emp = await createEmployee({
      name: newEmpForm.name,
      phone: newEmpForm.phone.replace(/\D/g, ""),
      email: newEmpForm.email,
      hourly_rate: Number(newEmpForm.hourly_rate) || 0,
      role: "crew",
      active: true,
      auto_text_enabled: true,
    });
    if (emp) {
      setEmployees(prev => [...prev, emp]);
      setShowAddEmployee(false);
      setNewEmpForm({ name: "", phone: "", email: "", hourly_rate: "" });
    }
  };

  // ─── RENDER EMPLOYEE PORTAL ───
  const renderEmployees = () => {
    const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - 7);
    const weekCards = timecards.filter(tc => new Date(tc.work_date) >= weekStart);
    const weekHours = weekCards.reduce((acc, tc) => {
      acc[tc.employee_name || "Unknown"] = (acc[tc.employee_name || "Unknown"] || 0) + Number(tc.hours || 0);
      return acc;
    }, {});

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={styles.sectionTitle}>Team Portal</h2>
          <div style={{ display: "flex", gap: 8 }}>
            {["timecards", "team", "budgets"].map(v => (
              <button key={v} onClick={() => setEmpView(v)} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${empView === v ? COLORS.honey : "rgba(155,142,130,0.2)"}`, background: empView === v ? "rgba(196,145,59,0.08)" : COLORS.white, color: empView === v ? COLORS.honey : COLORS.stone, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", textTransform: "capitalize" }}>
                {v}
              </button>
            ))}
          </div>
        </div>

        {empView === "timecards" && (
          <>
            {/* AI Time Entry */}
            <div style={{ ...styles.card, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.bark, marginBottom: 10 }}>Log Time</div>
              <p style={{ fontSize: 12, color: COLORS.stone, marginBottom: 10 }}>Type naturally — "8 hrs Mitchell framing" or "half day Park demo"</p>
              <div style={{ display: "flex", gap: 8 }}>
                <select value={timeInputEmployee} onChange={e => setTimeInputEmployee(e.target.value)}
                  style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(155,142,130,0.3)", fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: COLORS.cream, width: 160 }}>
                  <option value="">Select crew...</option>
                  {employees.filter(e => e.active).map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
                <input value={timeInput} onChange={e => setTimeInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !parsingTime && handleTimeEntry()}
                  placeholder="8 hrs Mitchell framing..."
                  style={{ ...styles.input, flex: 1 }} />
                <button onClick={() => timePhotoRef.current?.click()} title="Attach work photos"
                  style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid rgba(155,142,130,0.3)", background: timeInputPhotos.length > 0 ? "rgba(74,124,89,0.08)" : COLORS.cream, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0, color: timeInputPhotos.length > 0 ? COLORS.pine : COLORS.stone, position: "relative" }}>
                  📷{timeInputPhotos.length > 0 && <span style={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%", background: COLORS.pine, color: COLORS.white, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{timeInputPhotos.length}</span>}
                </button>
                <input ref={timePhotoRef} type="file" multiple accept="image/*" onChange={handleTimePhotoAdd} style={{ display: "none" }} />
                <button onClick={handleTimeEntry} disabled={parsingTime || !timeInput.trim() || !timeInputEmployee}
                  style={{ ...styles.primaryBtn, padding: "10px 20px", opacity: parsingTime ? 0.6 : 1 }}>
                  {parsingTime ? "Parsing..." : "Log"}
                </button>
              </div>
              {/* Photo previews */}
              {timeInputPhotos.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  {timeInputPhotos.map((p, i) => (
                    <div key={i} style={{ width: 48, height: 48, borderRadius: 6, overflow: "hidden", position: "relative", border: "1px solid rgba(155,142,130,0.2)" }}>
                      <img src={p.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button onClick={() => setTimeInputPhotos(prev => prev.filter((_, j) => j !== i))}
                        style={{ position: "absolute", top: 1, right: 1, width: 14, height: 14, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", cursor: "pointer", fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                    </div>
                  ))}
                  <span style={{ fontSize: 11, color: COLORS.stone, alignSelf: "center" }}>{timeInputPhotos.length} photo{timeInputPhotos.length > 1 ? "s" : ""} attached</span>
                </div>
              )}
            </div>

            {/* Weekly Summary */}
            {Object.keys(weekHours).length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(Object.keys(weekHours).length, 4)}, 1fr)`, gap: 16, marginBottom: 20 }}>
                {Object.entries(weekHours).map(([name, hours], i) => (
                  <div key={i} style={{ ...styles.card, textAlign: "center" }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.bark, marginBottom: 4 }}>{name}</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.honey, fontFamily: "'Playfair Display', serif" }}>{hours}<span style={{ fontSize: 14, color: COLORS.stone }}>hrs</span></div>
                    <div style={{ fontSize: 11, color: COLORS.stone }}>this week</div>
                  </div>
                ))}
              </div>
            )}

            {/* Timecard Table */}
            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <span style={styles.label}>Recent Timecards</span>
                <span style={{ fontSize: 11, color: COLORS.stone }}>{timecards.filter(t => !t.approved).length} pending approval</span>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid rgba(155,142,130,0.1)" }}>
                    {["Employee", "Date", "Job", "Task", "Hours", "Cost", "Source", ""].map(h => (
                      <th key={h} style={{ ...styles.label, padding: "8px 8px", textAlign: "left", fontSize: 10 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timecards.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: COLORS.stone }}>No timecards yet. Log time above or employees can text in.</td></tr>
                  )}
                  {timecards.map(tc => (
                    <tr key={tc.id} style={{ borderBottom: "1px solid rgba(155,142,130,0.08)", background: tc.approved ? "transparent" : "rgba(196,145,59,0.02)" }}>
                      <td style={{ padding: "10px 8px", fontWeight: 600, fontSize: 13 }}>{tc.employee_name}</td>
                      <td style={{ padding: "10px 8px", fontSize: 12, color: COLORS.stone }}>{tc.work_date}</td>
                      <td style={{ padding: "10px 8px" }}>
                        {tc.job_code && <span style={{ padding: "2px 6px", borderRadius: 4, background: "rgba(196,145,59,0.1)", color: COLORS.honey, fontSize: 10, fontWeight: 700, fontFamily: "monospace" }}>{tc.job_code}</span>}
                      </td>
                      <td style={{ padding: "10px 8px", fontSize: 12, color: COLORS.stone }}>
                        {tc.task}
                        {tc.photo_urls?.length > 0 && (
                          <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
                            {tc.photo_urls.map((url, pi) => (
                              <a key={pi} href={url} target="_blank" rel="noopener noreferrer" style={{ display: "block", width: 24, height: 24, borderRadius: 4, overflow: "hidden", border: "1px solid rgba(155,142,130,0.2)" }}>
                                <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              </a>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 8px", fontWeight: 600, fontSize: 13 }}>{tc.hours}</td>
                      <td style={{ padding: "10px 8px", fontWeight: 600, fontSize: 12, color: Number(tc.total_cost) > 0 ? COLORS.bark : COLORS.stone }}>
                        {Number(tc.total_cost) > 0 ? `$${Number(tc.total_cost).toLocaleString()}` : "—"}
                      </td>
                      <td style={{ padding: "10px 8px" }}>
                        <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: tc.source === "sms" ? "rgba(59,124,196,0.08)" : tc.source === "auto_text" ? "rgba(74,124,89,0.08)" : "rgba(155,142,130,0.08)", color: tc.source === "sms" ? COLORS.blue : tc.source === "auto_text" ? COLORS.green : COLORS.stone, fontWeight: 600 }}>
                          {tc.source}
                        </span>
                      </td>
                      <td style={{ padding: "10px 8px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          {!tc.approved && (
                            <button onClick={async () => { const u = await approveTimecard(tc.id); if (u) setTimecards(prev => prev.map(t => t.id === tc.id ? u : t)); }}
                              style={{ padding: "3px 8px", borderRadius: 4, border: "none", background: COLORS.pine, color: COLORS.white, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                              Approve
                            </button>
                          )}
                          {tc.approved && <span style={{ fontSize: 10, color: COLORS.green, fontWeight: 600 }}>Approved</span>}
                          <button onClick={async () => { if (window.confirm("Delete this timecard?")) { const ok = await deleteTimecard(tc.id); if (ok) setTimecards(prev => prev.filter(t => t.id !== tc.id)); }}}
                            style={{ padding: "3px 6px", borderRadius: 4, border: "none", background: "transparent", color: COLORS.red, fontSize: 10, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                            ×
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {empView === "team" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={styles.label}>Crew Members</span>
              <button onClick={() => setShowAddEmployee(true)} style={styles.primaryBtn}><Icons.Plus /> Add Crew</button>
            </div>

            {employees.length === 0 && (
              <div style={{ ...styles.card, textAlign: "center", padding: 36, color: COLORS.stone }}>
                No employees yet. Add crew members to enable time tracking and auto-text reminders.
              </div>
            )}

            {employees.map(emp => (
              <div key={emp.id} style={{ ...styles.card, display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: emp.role === "owner" ? COLORS.bark : COLORS.honey, color: COLORS.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                  {emp.name?.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.bark }}>{emp.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.stone }}>
                    {emp.role} · ${emp.hourly_rate}/hr · {emp.phone || "no phone"}
                    {emp.auto_text_enabled && <span style={{ color: COLORS.pine }}> · auto-text on</span>}
                  </div>
                </div>
                <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: emp.active ? "rgba(74,124,89,0.08)" : "rgba(155,142,130,0.1)", color: emp.active ? COLORS.green : COLORS.stone }}>
                  {emp.active ? "Active" : "Inactive"}
                </span>
              </div>
            ))}

            {/* Add Employee Modal */}
            <AddEmployeeModal
              show={showAddEmployee}
              onClose={() => setShowAddEmployee(false)}
              onCreate={handleAddEmployee}
              form={newEmpForm}
              setForm={setNewEmpForm}
              styles={styles}
            />
          </div>
        )}

        {empView === "budgets" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {liveProjects.length === 0 && (
              <div style={{ ...styles.card, textAlign: "center", padding: 36, color: COLORS.stone }}>
                No active projects. Projects are created when quotes are won.
              </div>
            )}
            {liveProjects.map(job => {
              const spent = Number(job.total_material_cost || 0) + Number(job.total_labor_cost || 0) + Number(job.total_sub_cost || 0) + Number(job.total_other_cost || 0);
              const budget = Number(job.bid_amount || 0);
              const pctSpent = budget > 0 ? (spent / budget) * 100 : 0;
              const laborHrs = timecards.filter(t => t.project_id === job.id).reduce((s, t) => s + Number(t.hours || 0), 0);
              return (
                <div key={job.id} style={styles.card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 16 }}>
                    <div>
                      <span style={{ padding: "3px 8px", borderRadius: 4, background: "rgba(196,145,59,0.1)", color: COLORS.honey, fontSize: 12, fontWeight: 700, fontFamily: "monospace" }}>{job.job_code}</span>
                      <h3 style={{ fontSize: 17, fontWeight: 700, color: COLORS.bark, marginTop: 6, fontFamily: "'Playfair Display', serif" }}>{job.project_name}</h3>
                    </div>
                    <StatusBadge status={job.status === "active" ? "Active Project" : job.status === "completed" ? "Completed" : "Lead"} />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 16 }}>
                    <div><div style={{ fontSize: 11, color: COLORS.stone, textTransform: "uppercase", letterSpacing: 1 }}>Budget</div><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.bark }}>${budget.toLocaleString()}</div></div>
                    <div><div style={{ fontSize: 11, color: COLORS.stone, textTransform: "uppercase", letterSpacing: 1 }}>Spent</div><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.honey }}>${spent.toLocaleString()}</div></div>
                    <div><div style={{ fontSize: 11, color: COLORS.stone, textTransform: "uppercase", letterSpacing: 1 }}>Remaining</div><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.green }}>${(budget - spent).toLocaleString()}</div></div>
                    <div><div style={{ fontSize: 11, color: COLORS.stone, textTransform: "uppercase", letterSpacing: 1 }}>Labor Hours</div><div style={{ fontSize: 20, fontWeight: 700, color: COLORS.bark }}>{laborHrs}</div></div>
                  </div>

                  {budget > 0 && (<>
                    <div style={{ height: 8, background: "rgba(155,142,130,0.1)", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.min(pctSpent, 100)}%`, background: pctSpent > 85 ? COLORS.red : COLORS.honey, borderRadius: 4, transition: "width 0.6s ease" }} />
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.stone, marginTop: 4, marginBottom: 16 }}>{Math.round(pctSpent)}% of budget used</div>
                  </>)}

                  {/* Design & Permitting */}
                  <PermitTracker
                    project={job}
                    onProjectUpdate={(updated) => setLiveProjects(prev => prev.map(p => p.id === updated.id ? updated : p))}
                    styles={styles}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ ...styles.app, position: "relative" }}>
      {!isAdmin && <TradeBackground tradeSlug={activeTrade?.slug || "decks"} />}
      <style>{`
        * { box-sizing: border-box; }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
        @keyframes heroScroll {
          0%, 100% { transform: translateX(-50%) translateY(0); opacity: 0.45; }
          50%       { transform: translateX(-50%) translateY(6px); opacity: 0.8; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        input:focus, textarea:focus, select:focus { border-color: ${COLORS.honey} !important; box-shadow: 0 0 0 3px rgba(196,145,59,0.12) !important; outline: none; }
        button:hover { filter: brightness(1.04); }
        button:active { transform: scale(0.98); }
        tr:hover td { background: rgba(196,145,59,0.02) !important; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-thumb { background: rgba(155,142,130,0.3); border-radius: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
        @media (min-width: 901px) {
          .mobile-menu-btn { display: none !important; }
        }
        @media (max-width: 820px) {
          .quote-grid { grid-template-columns: 1fr !important; }
          .quote-grid-right { position: static !important; }
        }
        @media (max-width: 640px) {
          .trade-switcher-bar { display: none !important; }
        }
      `}</style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logo}>
          <Icons.Mountain />
          <div>
            <div style={styles.logoText}>ULTRALIFE TRADES</div>
            {activeMarket && activeTrade && (
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", opacity: 0.55, marginTop: -2 }}>
                {activeMarket.displayName} · {activeTrade.displayName}
              </div>
            )}
          </div>
        </div>
        {/* Trade switcher (public) */}
        {!isAdmin && activeMarket && (
          <div className="trade-switcher-bar" style={{ display: "flex", gap: 2, marginLeft: "auto", marginRight: 16, background: "rgba(0,0,0,0.2)", borderRadius: 10, padding: 4 }}>
            {activeMarket.trades.map(t => (
              <button key={t} onClick={() => switchTrade?.(t)} style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: activeTrade?.slug === t ? "rgba(255,255,255,0.92)" : "transparent", color: activeTrade?.slug === t ? COLORS.bark : "rgba(255,255,255,0.38)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textTransform: "capitalize", transition: "all 0.2s" }}>
                {t}
              </button>
            ))}
          </div>
        )}

        <nav style={styles.nav} className="desktop-nav">
          {navItems.map(item => (
            <button key={item.id} onClick={() => switchView(item.id)} style={styles.navBtn(currentView === item.id)}>
              {item.icon}
              {item.label}
            </button>
          ))}
          <button
            onClick={() => window.location.href = '/work-with-us'}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: 8,
              border: "1px solid rgba(196,145,59,0.4)",
              background: "transparent", color: COLORS.honey,
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s",
              marginLeft: 4,
            }}
          >
            For Contractors
          </button>
          {isAdmin ? (
            <button onClick={onLogout} style={{ ...styles.navBtn(false), opacity: 0.6, marginLeft: 8 }}>
              <Icons.LogOut /> Sign Out
            </button>
          ) : (
            <button onClick={() => setShowLogin(true)} style={{ ...styles.navBtn(false), opacity: 0.6, marginLeft: 8 }}>
              <Icons.Lock /> Admin
            </button>
          )}
        </nav>

        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={styles.mobileMenuBtn}>
          {mobileMenuOpen ? <Icons.X /> : <Icons.Menu />}
        </button>
      </header>

      {/* Login Modal */}
      {showLogin && !isAdmin && LoginScreen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowLogin(false)}>
          <div onClick={e => e.stopPropagation()}>
            <LoginScreen onLogin={() => setShowLogin(false)} />
          </div>
        </div>
      )}

      {/* Booking Modal — Stripe deposit */}
      <BookingModal
        show={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        onSubmit={handleBookProject}
        loading={bookingLoading}
        error={bookingError}
        name={bookingName} setName={setBookingName}
        email={bookingEmail} setEmail={setBookingEmail}
        phone={bookingPhone} setPhone={setBookingPhone}
        totalEstimate={totalEstimate}
        tradeName={activeTrade?.displayName}
        marketName={activeMarket?.displayName}
        styles={styles}
      />

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div style={{ background: COLORS.bark, padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
          {navItems.map(item => (
            <button key={item.id} onClick={() => switchView(item.id)} style={{ ...styles.navBtn(currentView === item.id), width: "100%", justifyContent: "flex-start" }}>
              {item.icon}
              {item.label}
            </button>
          ))}
          <button
            onClick={() => window.location.href = '/work-with-us'}
            style={{ ...styles.navBtn(false), width: "100%", justifyContent: "flex-start", color: COLORS.honey, marginTop: 4 }}
          >
            For Contractors
          </button>
        </div>
      )}

      {/* Body */}
      <main style={styles.body}>
        {/* ── Customer landing page (non-admin, quote view only) ── */}
        {!isAdmin && currentView === "quote" && (
          <>
            <LandingHero
              market={activeMarket}
              trade={activeTrade}
              onCTAClick={scrollToQuote}
            />
            <div style={styles.container}>
              <ServicesGrid
                market={activeMarket}
                activeTrade={activeTrade}
                onTradeSelect={(slug) => {
                  switchTrade?.(slug);
                  setTimeout(scrollToQuote, 150);
                }}
              />
              <HowItWorks onCTAClick={scrollToQuote} />
              {/* Quote builder anchor */}
              <div
                ref={quoteRef}
                style={{
                  scrollMarginTop: 80,
                  paddingTop: 8,
                  borderTop: "2px solid rgba(196,145,59,0.2)",
                  marginTop: 8,
                }}
              >
                <div style={{ textAlign: "center", padding: "32px 0 24px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 3, color: COLORS.honey, marginBottom: 10 }}>
                    Start Your Project
                  </div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(24px, 3.5vw, 38px)", fontWeight: 800, color: COLORS.bark, margin: "0 0 12px" }}>
                    Get Your Free Estimate
                  </h2>
                  <p style={{ fontSize: 15, color: COLORS.stone, maxWidth: 480, margin: "0 auto" }}>
                    Answer 4 quick questions and receive a real, fixed-bid price for your{" "}
                    {activeTrade?.name?.toLowerCase() || "project"} in {activeMarket?.displayName || "your area"}.
                  </p>
                </div>
                {renderQuoteBuilder()}
              </div>
            </div>
          </>
        )}

        {/* ── Admin views ── */}
        {(isAdmin || currentView !== "quote") && (
          <div style={styles.container}>
            {currentView === "quote" && renderQuoteBuilder()}
            {isAdmin && currentView === "crm" && renderCRM()}
            {isAdmin && currentView === "visualizer" && <ProjectVisualizer customer={selectedCustomer} />}
            {isAdmin && currentView === "invoices" && renderInvoices()}
            {isAdmin && currentView === "employees" && renderEmployees()}
            {isAdmin && currentView === "capacity" && <CapacityDashboard styles={styles} />}
            {isAdmin && currentView === "owner-chat" && <OwnerChat />}
            {isAdmin && currentView === "admin" && <AdminPanel />}
          </div>
        )}
      </main>

      {/* Footer */}
      <SiteFooter
        market={activeMarket}
        trade={activeTrade}
        onTradeSelect={(slug) => {
          switchTrade?.(slug);
          setTimeout(scrollToQuote, 150);
        }}
      />
    </div>
  );
}
